<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useNuxtApp } from '#app'
import { getGoodDetail } from '@/api'
import {
  confirmOrder,
  getItemSpuDetail,
  buyNowConfirmOrder,
  buildFacebookEventPayload,
  sendFacebookEventSafely,
  sendTikTokEventSafely,
} from '@/api/mall'
import config from '~/config/index'
import { ElLoading, ElRate, ElButton, ElDivider, ElMessage, ElDialog, ElScrollbar, type ScrollbarInstance } from 'element-plus'
import RateItem from './mall/product/RateItem.vue'
import AddToCartModal from './common/AddToCartModal.vue'
import QuantitySelector from '@/components/common/QuantitySelector.vue'
import GoodsDetailBanner from '@/components/common/GoodsDetailBanner.vue'
import ProductAppBenefits from '@/components/common/ProductAppBenefits.vue'
import { useWindowResize } from '~/composables/useWindowResize'
import { trackEvent } from '~/utils/analytics'
import { createEventTrackingContext, getRedirectTrackingQuery } from '~/utils/eventTracking'
import { $t,transferOssUrl } from '~/utils/index'
import { useCdnDomain } from '~/composables/useCdnDomain'
import { useCartLimit } from '@/composables/useCartLimit'
import { reportAddCartVisit } from '@/utils/onlineVisit'
import { useHorizontalSwipe } from '@/composables/useHorizontalSwipe'
// 使用窗口大小监听 - 只用于小屏幕检测，避免样式冲突
const { windowWidth } = useWindowResize()
const { $customFetch } = useNuxtApp()

// 使用 CDN 域名替换
const { replaceCdnDomain } = useCdnDomain()

// 小屏幕检测 - 480px以下
const isSmallScreen = computed(() => windowWidth.value <= 480)
const isMobileLayout = computed(() => windowWidth.value <= 768)

const route = useRoute()
// SSR-provided product detail (optional)
const props = defineProps<{ 
  ssrProductDetail?: any
  isModal?: boolean // 是否在弹窗模式下显示
}>()

type PendingMobilePurchaseAction = 'cart' | 'buy' | null

// 定义 emit 事件
const emit = defineEmits(['addToCartSuccess', 'buyNowSuccess'])
const buyTotalQuantity = ref(1)
const cartDialogOpen = ref(false)
const mobileSkuPickerOpen = ref(false)
const pendingMobilePurchaseAction = ref<PendingMobilePurchaseAction>(null)
let bodyScrollLockState: {
  scrollTop: number
  body: Record<string, string>
  document: Record<string, string>
} | null = null
const mobileSkuPickerCloseRef = ref<HTMLButtonElement | null>(null)
const slideIndex = ref(0)
const summaryRef = ref<HTMLElement | null>(null)
const optionsRef = ref<HTMLElement | null>(null)
const purchaseActionsRef = ref<HTMLElement | null>(null)
const thumbsRef = ref<ScrollbarInstance | null>(null)
const reviewsRef = ref<any>(null)
const commentNum = ref(0)
const showZoomButton = ref(false)
const totalReviews = ref(0)
const averageRating = ref(0)
const showMobileStickyActions = ref(false)

let purchaseActionsObserver: IntersectionObserver | null = null

function disconnectPurchaseActionsObserver() {
  purchaseActionsObserver?.disconnect()
  purchaseActionsObserver = null
}

function updateStickyActionsFromRect() {
  if (
    typeof window === 'undefined'
    || props.isModal
    || !isMobileLayout.value
    || !purchaseActionsRef.value
  ) {
    showMobileStickyActions.value = false
    return
  }

  const rect = purchaseActionsRef.value.getBoundingClientRect()
  const actionsAreVisible = rect.bottom > 0 && rect.top < window.innerHeight
  showMobileStickyActions.value = !actionsAreVisible
}

function observePurchaseActionsVisibility() {
  disconnectPurchaseActionsObserver()
  updateStickyActionsFromRect()

  if (
    typeof window === 'undefined'
    || typeof IntersectionObserver === 'undefined'
    || props.isModal
    || !isMobileLayout.value
    || !purchaseActionsRef.value
  ) {
    return
  }

  purchaseActionsObserver = new IntersectionObserver(([entry]) => {
    showMobileStickyActions.value = Boolean(
      !props.isModal
      && isMobileLayout.value
      && entry
      && !entry.isIntersecting
    )
  }, { threshold: 0 })
  purchaseActionsObserver.observe(purchaseActionsRef.value)
}
const heroStyle = computed(() => {
  return { width: '100%' }
})
const detailId = computed(() => String(route.query?.detailId || '').trim())
const productInfo = ref<any>(props.ssrProductDetail || {})
// 详情页和快速查看弹窗统一使用商品接口评分，不依赖评论列表加载。
const ratingValue = computed(() => {
  const score = Number(productInfo.value.score)
  return Number.isFinite(score) ? Math.min(5, Math.max(0, score)) : 0
})
const productError = ref(false)
let detailRequestId = 0

function getProductDetailId(detail: any) {
  return String(
    detail?.id
    || detail?.dealerItemSpuId
    || detail?.spItemSpuId
    || detail?.spuId
    || '',
  ).trim()
}

function productDetailMatchesRoute(detail: any) {
  if (!detailId.value) return true
  return getProductDetailId(detail) === detailId.value
}

async function getDetail(targetDetailId = detailId.value) {
  const normalizedDetailId = String(targetDetailId || '').trim()
  if (!normalizedDetailId) return

  const requestId = ++detailRequestId
  const loadingInstance = ElLoading.service({
    lock: true,
    text: 'Loading',
    background: 'rgba(255, 255, 255, 0.5)',
  })

  try {
    const res = await getGoodDetail({
      param: {
        id: normalizedDetailId,
      }
    })
    if (requestId !== detailRequestId) return

    const skuIconUrl = ref("");
    // 处理新的数据结构
    if (res.data.dealerItemSkuDTOList) {
      res.data.dealerItemSkuDTOList = res.data.dealerItemSkuDTOList.map(
        (o: any) => {
          if (o.iconUrl) {
            skuIconUrl.value = o.iconUrl;
          }
          if (o.state == 0) {
            o.inventory = 0;
          }
          return o;
        }
      );
    }
    productInfo.value = res.data

    // 初始化规格选择
    initializeSpecSelection()

    productError.value = false
  }
  catch (error) {
    if (requestId === detailRequestId)
      productError.value = true
  }
  finally {
    loadingInstance.close()
  }
}




const selectedAttributes = ref<any>({})
const selectedSku = ref<any>(null)

function handleSelectSpecValue(spec: any, attrValue: any) {
  if (!isSpecValueAvailable(spec.id, attrValue.id)) return

  selectedAttributes.value = {
    ...selectedAttributes.value,
    [spec.id]: attrValue.id,
  }

  // 根据选中的规格匹配SKU
  matchSelectedSku()

  // 更新当前显示的图片
  updateCurrentImage(attrValue.skuImage)
}

function attrValueIsSelected(specId: string, attrValueId: string) {
  return String(selectedAttributes.value[specId] ?? '') === String(attrValueId)
}

function getSkuSpecPairSet(sku: any) {
  const pairs = String(sku?.spItemSkuSpecificationIds || '')
    .split(',')
    .map((item: string) => item.trim())
    .filter(Boolean)

  if (pairs.length) return new Set(pairs)

  return new Set((sku?.dealerItemSkuSpecReqDTOs || []).flatMap((spec: any) => {
    const specId = spec?.id
    const valueId = spec?.dealerItemSkuSpecDTO?.id
    return specId != null && valueId != null ? [`${specId}-${valueId}`] : []
  }))
}

function skuIsAvailable(sku: any) {
  return Boolean(sku && sku.state !== 0 && Number(sku.inventory || 0) > 0)
}

// 检查某个规格值是否有可用库存
function isSpecValueAvailable(specId: string, attrValueId: string) {
  if (!productInfo.value.dealerItemSkuDTOList) return false

  const requiredPairs = Object.entries(selectedAttributes.value)
    .filter(([selectedSpecId]) => String(selectedSpecId) !== String(specId))
    .map(([selectedSpecId, selectedValueId]) => `${selectedSpecId}-${selectedValueId}`)
  requiredPairs.push(`${specId}-${attrValueId}`)

  // 规格按钮的可用性同时考虑其他已选规格，避免多规格组合误判。
  return productInfo.value.dealerItemSkuDTOList.some((sku: any) => {
    if (!skuIsAvailable(sku)) return false
    const skuPairs = getSkuSpecPairSet(sku)
    return requiredPairs.every(pair => skuPairs.has(pair))
  })
}

// 更新当前显示的图片
function updateCurrentImage(imageUrl: string) {
  if (!imageUrl) return

  // 找到对应的图片索引
  const imageIndex = productImages.value.findIndex((img: any) => img.iconUrl === imageUrl)
  if (imageIndex !== -1) {
    slideIndex.value = imageIndex
  }
}

// 根据选中的规格匹配对应的SKU
function matchSelectedSku() {
  if (!productInfo.value.dealerItemSkuDTOList) return

  const selectedSkuId = findMatchingSku()
  selectedSku.value = selectedSkuId
    ? productInfo.value.dealerItemSkuDTOList.find((sku: any) => sku.id === selectedSkuId) || null
    : null
}

// 将指定 SKU 的规格同步到选项上
function applySkuSelection(sku: any) {
  if (!sku) return

  const nextSelection: Record<string, string> = {}
  ;(sku.dealerItemSkuSpecReqDTOs || []).forEach((spec: any) => {
    if (spec.dealerItemSkuSpecDTO) {
      nextSelection[spec.id] = spec.dealerItemSkuSpecDTO.id
    }
  })

  if (!Object.keys(nextSelection).length) {
    const skuPairs = getSkuSpecPairSet(sku)
    ;(productInfo.value.dealerItemSkuSpecDTOs || []).forEach((specGroup: any) => {
      const matchedValue = (specGroup.dealerItemSkuSpecReqDTOs || []).find((specValue: any) =>
        skuPairs.has(`${specGroup.id}-${specValue.id}`)
      )
      if (matchedValue) nextSelection[specGroup.id] = matchedValue.id
    })
  }

  if (Object.keys(nextSelection).length > 0) {
    selectedAttributes.value = nextSelection
  }
  selectedSku.value = sku
}

// 查找匹配的SKU - 根据 spItemSkuSpecificationIds 匹配
function findMatchingSku() {
  if (!productInfo.value.dealerItemSkuDTOList) return null

  const selectedSpecIds = Object.keys(selectedAttributes.value).map(specGroupId =>
    `${specGroupId}-${selectedAttributes.value[specGroupId]}`
  )

  if (selectedSpecIds.length !== (productInfo.value.dealerItemSkuSpecDTOs?.length || 0)) {
    return null
  }

  // 在 SKU 列表中查找匹配的
  const matchedSku = productInfo.value.dealerItemSkuDTOList.find((sku: any) => {
    const skuSpecIds = getSkuSpecPairSet(sku)
    return skuSpecIds.size === selectedSpecIds.length
      && selectedSpecIds.every(pair => skuSpecIds.has(pair))
  })

  return matchedSku?.id
}

// 初始化规格选择
function initializeSpecSelection() {
  if (!productInfo.value.dealerItemSkuDTOList) return
  if (!productInfo.value.dealerItemSkuSpecDTOs) return

  selectedSku.value = null
  selectedAttributes.value = {}
  slideIndex.value = 0
}

// 缩略图点击时同步 SKU 规格
function handleSkuImageClick(index: number) {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }

  slideIndex.value = index

  const clickedSkuId = productImages.value?.[index]?.id
  if (!clickedSkuId) return

  const clickedSku = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => sku.id === clickedSkuId)
  if (clickedSku) {
    applySkuSelection(clickedSku)
  }
}

// 当前基础价格
const currentPrice = computed(() => {
  if (selectedSku.value) {
    return selectedSku.value.finalPrice ?? selectedSku.value.price ?? selectedSku.value.skuPrice
  }
  return productInfo.value.finalPrice || productInfo.value.skuPrice || productInfo.value.price || getMinPrice(productInfo.value)
})

const currentOriginalPrice = computed(() => {
  const originalPrice = selectedSku.value?.price
    ?? selectedSku.value?.skuPrice
    ?? productInfo.value.price
    ?? productInfo.value.skuPrice
  const sellingPrice = Number(currentPrice.value || 0)
  const originalPriceNumber = Number(originalPrice || 0)

  return originalPriceNumber > sellingPrice ? originalPriceNumber : null
})

// 当前会员价
const currentMemberPrice = computed(() => {
  if (selectedSku.value && selectedSku.value.discountPrice) {
    return selectedSku.value.discountPrice
  }
  return productInfo.value.discountPrice
})

const vipIconUrl = 'http://sztjyshop.oss-cn-guangzhou.aliyuncs.com/imgs/2026/02-04/upload_pic_1770171482456_vip.png'

const showMemberPrice = computed(() => {
  const basePrice = Number(currentPrice.value)
  const memberPrice = Number(currentMemberPrice.value)
  if (!memberPrice || !basePrice) return false
  return memberPrice !== basePrice
})

const formatProductDisplayAmount = (amount: number | string) =>
  Math.round(Number(amount || 0)).toLocaleString('zh-TW')

// 当前SKU是否可用
const currentSkuAvailable = computed(() => {
  if (selectedSku.value) return skuIsAvailable(selectedSku.value)
  if (productInfo.value.dealerItemSkuSpecDTOs?.length) return false
  return productInfo.value.inStock !== false
})

const allSkusUnavailable = computed(() => {
  const skuList = productInfo.value.dealerItemSkuDTOList || []
  return skuList.length > 0 && skuList.every((sku: any) => !skuIsAvailable(sku))
})

const selectedSkuUnavailable = computed(() =>
  !allSkusUnavailable.value && Boolean(selectedSku.value) && !currentSkuAvailable.value
)

const shouldOfferReselection = computed(() => selectedSkuUnavailable.value)

const productSpecs = computed(() => {
  if (!productInfo.value.dealerItemSkuSpecDTOs) return []

  // 从 dealerItemSkuSpecDTOs 构建规格选项
  return productInfo.value.dealerItemSkuSpecDTOs.map((specGroup: any) => {
    return {
      id: specGroup.id,
      specName: specGroup.specName,
      children: (specGroup.dealerItemSkuSpecReqDTOs || [])
        .map((specValue: any) => {
          // 售罄规格也必须保留，才能呈现正常 / 选中 / 不可选三种状态。
          const matchingSku = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => {
            return getSkuSpecPairSet(sku).has(`${specGroup.id}-${specValue.id}`)
          })
          
          return {
            id: specValue.id,
            specName: specValue.specName,
            skuImage: matchingSku?.iconUrl || ''
          }
        })
    }
  })
})

const hasCompleteSpecSelection = computed(() =>
  productSpecs.value.length > 0
  && productSpecs.value.every((spec: any) => Boolean(selectedAttributes.value?.[spec.id]))
)

const needsSkuSelection = computed(() =>
  productSpecs.value.length > 0
  && !hasCompleteSpecSelection.value
  && !allSkusUnavailable.value
)

const purchaseActionDisabled = computed(() => {
  if (allSkusUnavailable.value) return true
  if (productSpecs.value.length > 0) {
    return Boolean(selectedSku.value) && !currentSkuAvailable.value
  }
  return !currentSkuAvailable.value
})

const quantityDisabled = computed(() =>
  allSkusUnavailable.value
  || selectedSkuUnavailable.value
  || (productSpecs.value.length === 0 && !currentSkuAvailable.value)
)

const quantityMax = computed(() => {
  const selectedInventory = Number(selectedSku.value?.inventory || 0)
  if (selectedInventory > 0) return selectedInventory

  const availableInventory = (productInfo.value.dealerItemSkuDTOList || [])
    .filter(skuIsAvailable)
    .reduce((max: number, sku: any) => Math.max(max, Number(sku?.inventory || 0)), 0)

  return availableInventory || Number(productInfo.value.inventory || 0) || 1
})

async function requestSkuSelection(action: PendingMobilePurchaseAction = null) {
  if (!needsSkuSelection.value) return false

  if (isMobileLayout.value) {
    await openMobileSkuPicker(action)
    return true
  }

  await nextTick()
  optionsRef.value?.querySelector<HTMLElement>('.option-iteme:not(:disabled)')?.focus()
  optionsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return true
}

function selectedSpecValueText(spec: any) {
  const selectedValueId = selectedAttributes.value?.[spec.id]
  return spec.children.find((item: any) => String(item.id) === String(selectedValueId))?.specName || ''
}

const selectedSpecSummary = computed(() => productSpecs.value
  .map((spec: any) => {
    const selectedValue = selectedSpecValueText(spec)
    return selectedValue ? `${spec.specName}/${selectedValue}` : ''
  })
  .filter(Boolean)
  .join('、'))

const firstFiniteNumber = (...values: any[]) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue
    const parsedValue = Number(value)
    if (Number.isFinite(parsedValue)) return parsedValue
  }
  return 0
}

const displaySalesCount = computed(() => firstFiniteNumber(
  productInfo.value.salesSum,
  productInfo.value.salesNum,
  productInfo.value.saleAmount,
  productInfo.value.saleNum,
  productInfo.value.soldNum,
  productInfo.value.soldCount,
  productInfo.value.totalSales,
  productInfo.value.monthSold,
))

const displaySalesCountText = computed(() =>
  Math.max(0, displaySalesCount.value).toLocaleString('zh-TW')
)

function scrollToReviews() {
  reviewsRef.value?.$el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
}

async function handleReselectOptions() {
  selectedAttributes.value = {}
  selectedSku.value = null
  buyTotalQuantity.value = 1
  await nextTick()

  if (isMobileLayout.value) {
    await openMobileSkuPicker()
    return
  }

  optionsRef.value?.querySelector<HTMLElement>('.option-iteme:not(:disabled)')?.focus()
  optionsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

async function openMobileSkuPicker(action: PendingMobilePurchaseAction = null) {
  if (!isMobileLayout.value) return
  pendingMobilePurchaseAction.value = action === 'cart' || action === 'buy' ? action : null
  mobileSkuPickerOpen.value = true
  await nextTick()

  // Figma contract: reopen at the active flavor instead of resetting the
  // independently scrolling flavor viewport to the first row.
  if (typeof window !== 'undefined') {
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
  }
  scrollActiveSkuPillsIntoView()
  mobileSkuPickerCloseRef.value?.focus({ preventScroll: true })
}

function handleMobileSkuPickerOverlayTouchMove(event: TouchEvent) {
  const picker = mobileSkuPickerCloseRef.value?.closest<HTMLElement>('.mobile-sku-picker')
  const target = event.target

  // Keep the option list scrollable while preventing touches on the backdrop
  // from scrolling the product page underneath the picker.
  if (picker && target instanceof Node && picker.contains(target)) return
  event.preventDefault()
}

function lockBodyScroll() {
  if (typeof document === 'undefined' || typeof window === 'undefined' || bodyScrollLockState) return

  const body = document.body
  const documentElement = document.documentElement
  const scrollTop = window.scrollY || documentElement.scrollTop || 0

  bodyScrollLockState = {
    scrollTop,
    body: {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      touchAction: body.style.touchAction,
    },
    document: {
      overflow: documentElement.style.overflow,
      touchAction: documentElement.style.touchAction,
    },
  }

  body.style.position = 'fixed'
  body.style.top = `-${scrollTop}px`
  body.style.left = '0'
  body.style.right = '0'
  body.style.width = '100%'
  body.style.overflow = 'hidden'
  body.style.touchAction = 'none'
  documentElement.style.overflow = 'hidden'
  documentElement.style.touchAction = 'none'
}

function unlockBodyScroll() {
  if (typeof document === 'undefined' || typeof window === 'undefined' || !bodyScrollLockState) return

  const state = bodyScrollLockState
  const body = document.body
  const documentElement = document.documentElement

  Object.entries(state.body).forEach(([property, value]) => {
    body.style[property as any] = value
  })
  Object.entries(state.document).forEach(([property, value]) => {
    documentElement.style[property as any] = value
  })

  bodyScrollLockState = null
  window.scrollTo(0, state.scrollTop)
}

function scrollActiveSkuPillsIntoView() {
  const picker = mobileSkuPickerCloseRef.value?.closest<HTMLElement>('.mobile-sku-picker')
  const activePills = picker?.querySelectorAll<HTMLElement>('.mobile-sku-picker__pill.is-active')

  activePills?.forEach((pill) => {
    const values = pill.closest<HTMLElement>('.mobile-sku-picker__values')
    if (!values || values.scrollHeight <= values.clientHeight) return

    const valuesRect = values.getBoundingClientRect()
    const pillRect = pill.getBoundingClientRect()
    const pillIsVisible = pillRect.top >= valuesRect.top && pillRect.bottom <= valuesRect.bottom
    if (pillIsVisible) return

    const centeredOffset = pillRect.top - valuesRect.top
      - ((values.clientHeight - pillRect.height) / 2)
    const maxScrollTop = values.scrollHeight - values.clientHeight
    values.scrollTop = Math.max(0, Math.min(values.scrollTop + centeredOffset, maxScrollTop))
  })
}

function closeMobileSkuPicker() {
  mobileSkuPickerOpen.value = false
  pendingMobilePurchaseAction.value = null
}

function handleSkuPickerKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && mobileSkuPickerOpen.value) closeMobileSkuPicker()
}

async function handleMobilePickerAddToCart() {
  if (needsSkuSelection.value) {
    ElMessage.warning($t('actions.selectOptions'))
    return
  }
  closeMobileSkuPicker()
  await nextTick()
  await handleAddToCart({ fromPicker: true })
}

async function handleMobilePickerBuyNow() {
  if (needsSkuSelection.value) {
    ElMessage.warning($t('actions.selectOptions'))
    return
  }
  closeMobileSkuPicker()
  await nextTick()
  await jumpPayPage({ fromPicker: true })
}

async function handleMobilePickerConfirm() {
  if (needsSkuSelection.value) {
    ElMessage.warning($t('actions.selectOptions'))
    return
  }

  const action = pendingMobilePurchaseAction.value
  mobileSkuPickerOpen.value = false
  pendingMobilePurchaseAction.value = null
  await nextTick()

  if (action === 'cart')
    await handleAddToCart({ fromPicker: true })
  else if (action === 'buy')
    await jumpPayPage({ fromPicker: true })
}



function getMinPrice(item: any) {
  if (!item.dealerItemSkuDTOList) return 0;
  const priceNumArrs = item.dealerItemSkuDTOList
    ? item.dealerItemSkuDTOList.map((o: any) => o.price)
    : [];
  
  // 检查数组是否为空
  if (priceNumArrs.length === 0) return 0;
  
  const minPrice = priceNumArrs.reduce((prev: number, next: number) => {
    return Math.min(prev, next);
  });
  return minPrice || 0;
}



// 路由
const router = useRouter()


const handleChange = () => {
  // 处理数量变化
}

const handleReviewSubmitted = (review: any) => {
  // 处理新评论逻辑
  console.log('新评论提交:', review)
  // 可以在这里更新评论数量等
  if (reviewsRef.value) {
    // 刷新评论数据
    reviewsRef.value.refreshComments()
  }
}

// 获取评价数据的方法
const getReviewData = async () => {
  if (reviewsRef.value) {
    try {
      // 从RateItem组件获取评价数据
      const reviews = reviewsRef.value.getReviews()
      const avgRating = reviewsRef.value.getAverageRating()
      
      // 更新本地数据
      totalReviews.value = reviews.length
      averageRating.value = parseFloat(avgRating) || 0
      commentNum.value = totalReviews.value
      
      console.log('评价数据:', {
        totalReviews: totalReviews.value,
        averageRating: averageRating.value,
        reviews: reviews
      })
    } catch (error) {
      console.error('获取评价数据失败:', error)
    }
  }
}

// 监听RateItem组件数据变化
const updateReviewData = () => {
  if (reviewsRef.value) {
    const reviews = reviewsRef.value.getReviews()
    const avgRating = reviewsRef.value.getAverageRating()
    
    totalReviews.value = reviews.length
    averageRating.value = parseFloat(avgRating) || 0
    commentNum.value = totalReviews.value
  }
}

// 图片导航函数
function previousImage() {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }
  if (slideIndex.value > 0) {
    slideIndex.value--
  }
}

function nextImage() {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }
  if (slideIndex.value < productImages.value.length - 1) {
    slideIndex.value++
  }
}

const {
  dragOffsetX: productGalleryDragOffsetX,
  isDragging: isProductGalleryDragging,
  isSettling: isProductGallerySettling,
  onClickCapture: onProductGalleryClickCapture,
  onTouchCancel: onProductGalleryTouchCancel,
  onTouchEnd: onProductGalleryTouchEnd,
  onTouchMove: onProductGalleryTouchMove,
  onTouchStart: onProductGalleryTouchStart,
} = useHorizontalSwipe({
  enabled: () => (
    isMobileLayout.value
    && !allSkuIconUrlsEmpty.value
    && productImages.value.length > 1
  ),
  canSwipeLeft: () => slideIndex.value < productImages.value.length - 1,
  canSwipeRight: () => slideIndex.value > 0,
  onSwipeLeft: nextImage,
  onSwipeRight: previousImage,
})
const productGalleryTrackStyle = computed(() => ({
  transform: `translate3d(calc(-100% + ${productGalleryDragOffsetX.value}px), 0, 0)`,
}))
const productGallerySlideOffsets = [-1, 0, 1]

// 缩略图滚动函数
function scrollThumbsLeft() {
  const scroller = thumbsRef.value?.wrapRef as HTMLElement | undefined
  if (scroller) {
    // 计算每个缩略图的宽度（包括gap）
    const thumbWidth = scroller.scrollWidth / productImages.value.length
    const scrollAmount = thumbWidth * 4 // 每次滚动4个缩略图的宽度
    scroller.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    })
  }
}

function scrollThumbsRight() {
  const scroller = thumbsRef.value?.wrapRef as HTMLElement | undefined
  if (scroller) {
    // 计算每个缩略图的宽度（包括gap）
    const thumbWidth = scroller.scrollWidth / productImages.value.length
    const scrollAmount = thumbWidth * 4 // 每次滚动4个缩略图的宽度
    scroller.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    })
  }
}

// 判断是否需要显示滚动按钮（超过4张时）
const showThumbScrollButtons = computed(() => {
  return !isSmallScreen.value && productImages.value.length > 4
})

onMounted(async () => {
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', handleSkuPickerKeydown)
    observePurchaseActionsVisibility()
  }

  if (props.ssrProductDetail && productDetailMatchesRoute(props.ssrProductDetail)) {
    productInfo.value = props.ssrProductDetail
    initializeSpecSelection()
    productError.value = false
  } else {
    await getDetail()
  }

  // Analytics 埋点：查看商品
  if (productInfo.value) {

    let val= productInfo.value.price|| 0;
    if(!val && productInfo.value?.dealerItemSkuDTOList&&productInfo.value?.dealerItemSkuDTOList.length>0) {
      val = productInfo.value?.dealerItemSkuDTOList[0]?.price|| 0;
    }
    trackEvent('ViewContent', {
      contents: [{
        content_id: productInfo.value.id,
        content_name: productInfo.value.title || productInfo.value.subject,
        content_type: 'product'
      }],
      value:val,
      currency: 'TWD',
      // 保留Google Analytics向后兼容性
      category: 'Product',
      action: 'view_product',
      label: productInfo.value.title || productInfo.value.subject
    })
  }

  // 延迟获取评价数据，确保RateItem组件已经加载完成
  setTimeout(() => {
    getReviewData()
  }, 1000)

  // 设置定期更新评价数据
  reviewUpdateInterval = setInterval(() => {
    updateReviewData()
  }, 3000) // 每3秒更新一次
})

// react to prop updates when navigating client-side
watch(() => props.ssrProductDetail, (val) => {
  if (val && productDetailMatchesRoute(val)) {
    ++detailRequestId
    productInfo.value = val
    initializeSpecSelection()
    productError.value = false
  }
})

watch([isMobileLayout, () => props.isModal], ([isMobile]) => {
  nextTick(observePurchaseActionsVisibility)
  if (!isMobile) closeMobileSkuPicker()
})

watch(mobileSkuPickerOpen, (isOpen) => {
  if (isOpen) {
    lockBodyScroll()
    return
  }

  unlockBodyScroll()
})

// 同一详情页内切换推荐商品时，路由组件会被复用，需要主动加载新商品。
watch(detailId, async (newId, oldId) => {
  if (!newId || newId === oldId || props.isModal) return

  await getDetail(newId)
  setTimeout(() => {
    getReviewData()
  }, 1500)
})

// 检查所有SKU的iconUrl是否都为空
const allSkuIconUrlsEmpty = computed(() => {
  if (!productInfo.value.dealerItemSkuDTOList || productInfo.value.dealerItemSkuDTOList.length === 0) {
    return false
  }
  // 检查所有SKU的iconUrl是否都为空或无效
  return productInfo.value.dealerItemSkuDTOList.every((sku: any) => {
    const iconUrl = sku.iconUrl && sku.iconUrl.trim()
    return !iconUrl
  })
})

const productImages = computed(() => {
  // 如果所有SKU的iconUrl都为空，使用SPU的iconUrl作为唯一图片
  if (allSkuIconUrlsEmpty.value && productInfo.value.iconUrl) {
    // 使用第一个SKU的ID（如果有）或者使用SPU的ID
    const firstSku = productInfo.value.dealerItemSkuDTOList?.[0]
    return [{
      iconUrl: productInfo.value.iconUrl,
      id: firstSku?.id || productInfo.value.id,
      dealerItemSkuSpecReqDTOs: firstSku?.dealerItemSkuSpecReqDTOs,
      dealerItemSpuId: productInfo.value.id,
      spItemSkuSpecificationIds: firstSku?.spItemSkuSpecificationIds,
    }]
  }

  const attrMap: any[] = []

  productInfo.value.dealerItemSkuDTOList?.forEach((attr: any) => {
    const imageUrl = attr.iconUrl && attr.iconUrl.trim()
    if (!imageUrl) return

    attrMap.push({
      iconUrl: imageUrl,
      id: attr.id,
      dealerItemSkuSpecReqDTOs: attr.dealerItemSkuSpecReqDTOs,
      dealerItemSpuId: attr.dealerItemSpuId,
      spItemSkuSpecificationIds: attr.spItemSkuSpecificationIds,
    })
  })

  return attrMap
})

// 定期更新评价数据
let reviewUpdateInterval: NodeJS.Timeout | null = null

// 组件卸载时清理定时器
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', handleSkuPickerKeydown)
  }
  disconnectPurchaseActionsObserver()
  unlockBodyScroll()
  if (reviewUpdateInterval) {
    clearInterval(reviewUpdateInterval)
  }
})

// 暴露方法给父组件
defineExpose({
  getReviewData,
  updateReviewData,
  getReviewStats: () => ({
    totalReviews: totalReviews.value,
    averageRating: averageRating.value,
    commentNum: commentNum.value
  })
})

const cartStore = useCartStore()
const { checkCanAddQuantity } = useCartLimit()
// 加入购物车 - 直接变更购物车数据，再打开抽屉
async function handleAddToCart(options: { fromPicker?: boolean } = {}) {
  if (isMobileLayout.value && !options.fromPicker) {
    await openMobileSkuPicker('cart')
    return
  }

  if (await requestSkuSelection('cart')) return

  if (!currentSkuAvailable.value) {
    ElMessage.warning($t('messages.specSoldOut'))
    return
  }
  console.log("selectedSkuselectedSku",selectedSku)
  console.log("productImages",productImages)

  // 校验 SKU：优先使用选中的SKU，否则回退到第一个可用的SKU
  let skuId = selectedSku.value?.id
  if (!skuId) {
    // 如果没有选中的SKU，尝试从SKU列表中获取第一个可用的SKU
    const firstAvailableSku = productInfo.value.dealerItemSkuDTOList?.find(
      (sku: any) => sku.state !== 0 && sku.inventory > 0
    )
    skuId = firstAvailableSku?.id
  }
  if (!skuId) {
    ElMessage.warning($t('messages.cannotGetSkuInfo'))
    return
  }

  const cartLimitCheck = await checkCanAddQuantity(buyTotalQuantity.value)
  if (!cartLimitCheck.allowed) {
    ElMessage.warning($t('messages.cartQuantityLimitExceeded', { max: cartLimitCheck.maxQuantity }))
    return
  }

  // 构建规格信息
  const specsInfo = buildSpecsInfo()

  // 判断登录态（与 AddToCartModal 保持一致）
  const isLoggedIn = typeof window !== 'undefined' && !!window.localStorage.getItem(config.appTokenKey)
  const addToCartTrackingContext = createEventTrackingContext()
  const currentSkuData = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => sku.id === skuId) || {}
  const addToCartFacebookCustomData = {
    value: currentSkuData.price || productInfo.value.price || 0,
    currency: 'TWD',
    contentIds: skuId ? [skuId] : [],
    contentType: 'product'
  }
  const addToCartTikTokCustomData = {
    ...addToCartFacebookCustomData,
    contentName: productInfo.value.title || productInfo.value.subject || ''
  }

  try {
    if (isLoggedIn) {
      // 已登录：直接调接口写入购物车
      const facebookConversionPayload = buildFacebookEventPayload({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartFacebookCustomData
      })
      const response = await $customFetch('/mall/admin/mallShoppingCart/add?isShop=1', {
        method: 'POST',
        body: {
          param: {
            buyAmount: buyTotalQuantity.value,
            specsInfo,
            dealerItemSkuId: skuId,
            dealerShopLiveStreamerId: '',
            eventId: addToCartTrackingContext.eventId,
            eventSourceUrl: typeof window !== 'undefined' ? window.location.href : addToCartTrackingContext.eventSourceUrl,
            facebookConversionRequestDTO: facebookConversionPayload?.body?.param || null
          }
        }
      })

      if (!response?.success) {
        ElMessage.error(response?.msg || $t('messages.addToCartFailed'))
        return
      }
      ElMessage.success($t('messages.addToCartSuccess'))
      void reportAddCartVisit()
      void sendTikTokEventSafely({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartTikTokCustomData
      })
      // Analytics 埋点：加购商品
      trackEvent('AddToCart', {
        contents: [{
          content_id: skuId,
          content_name: productInfo.value.title || productInfo.value.subject,
          content_type: 'product'
        }],
        value: addToCartFacebookCustomData.value,
        currency: 'TWD',
        eventId: addToCartTrackingContext.eventId,
        // 保留Google Analytics向后兼容性
        category: 'Product',
        action: 'add_to_cart',
        label: productInfo.value.title || productInfo.value.subject
      })
    } else {
      console.log('productInfo.value.sendTypes', productInfo.value)
      // 未登录：写入本地存储，与弹窗结构保持一致
      const LOCAL_CART_KEY = 'local_cart_items'
      // skuId 在前面已经处理了（优先使用 selectedSku，否则使用第一个可用SKU）
      const effectiveSkuId = skuId
      const newItem = {
        id: productInfo.value.id,
        title: productInfo.value.title || productInfo.value.subject,
        brand: 'SpStar',
        price: selectedSku.value?.price || productInfo.value.skuPrice || 0,
        buyAmount: buyTotalQuantity.value,
        image: selectedSku.value?.iconUrl || productInfo.value.iconUrl,
        options: specsInfo ? { specsInfo } : {},
        selectedSku: selectedSku.value,
        dealerItemSkuId: effectiveSkuId,
        spItemSpuId: productInfo.value.spItemSpuId || productInfo.value.id,
        dealerItemSpuId: productInfo.value.dealerItemSpuId || productInfo.value.id,
        dealerShopId: productInfo.value.dealerShopId,
        specsInfo,
        payWay: productInfo.value.payWay,
        sendTypes: productInfo.value.sendTypes
      }

      let cartItems = []
      try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(LOCAL_CART_KEY) : null
        cartItems = raw ? JSON.parse(raw) : []
      } catch (_) { cartItems = [] }

      // 与抽屉一致的合并规则：同 SKU + 同规格合并数量；若缺少 SKU，则退化为标题+规格比对
      const existingIndex = cartItems.findIndex((it: any) => {
        const sameSku = newItem.dealerItemSkuId && it.dealerItemSkuId === newItem.dealerItemSkuId
        const sameSpecs = it.specsInfo === newItem.specsInfo
        if (sameSku) return sameSpecs
        // 无法获取 SKU 时的兜底：按标题+规格合并，避免重复条目
        return !newItem.dealerItemSkuId && (it.title === newItem.title) && sameSpecs
      })
      if (existingIndex !== -1) {
        cartItems[existingIndex].buyAmount += newItem.buyAmount
      } else {
        cartItems.push(newItem)
      }
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(cartItems))
      }
      ElMessage.success($t('messages.addToCartSuccess'))
      void reportAddCartVisit()
      void sendFacebookEventSafely({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartFacebookCustomData
      })
      void sendTikTokEventSafely({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartTikTokCustomData
      })
      // Analytics 埋点：加购商品（未登录状态）
      trackEvent('AddToCart', {
        contents: [{
          content_id: effectiveSkuId,
          content_name: productInfo.value.title || productInfo.value.subject,
          content_type: 'product'
        }],
        value: addToCartFacebookCustomData.value,
        currency: 'TWD',
        eventId: addToCartTrackingContext.eventId,
        // 保留Google Analytics向后兼容性
        category: 'Product',
        action: 'add_to_cart',
        label: productInfo.value.title || productInfo.value.subject
      })
    }

    cartStore.getCartData()

    // 如果在弹窗模式下，触发事件让父组件处理
    if (props.isModal) {
      emit('addToCartSuccess')
      return
    }

    // 打开抽屉展示最新购物车（已添加完成，使用view模式）
    cartDialogOpen.value = true
  } catch (error: any) {
    ElMessage.error(error?.message || $t('messages.addToCartFailedRetry'))
  }
}





const getOptimizedProductImageUrl = (iconUrl: string, width: number) => {
  if (!iconUrl) return ''
  return replaceCdnDomain(transferOssUrl(iconUrl, width))
}

// 图片预览保留较高清晰度，但避免加载 OSS 原图。
const productImagePreviewList = computed(() => productImages.value
  .filter((item: any) => !!item.iconUrl)
  .map((item: any) => getOptimizedProductImageUrl(item.iconUrl, 705)))

const currentImageSourceUrl = computed(() => {
  const currentImage = productImages.value[slideIndex.value]
  return currentImage?.iconUrl || ''
})

// PC 主图最大展示 668px，按 2x 像素请求以兼顾清晰度和资源体积。
const currentImageUrl = computed(() => {
  return getOptimizedProductImageUrl(currentImageSourceUrl.value, 705)
})

function getProductGalleryImageUrl(offset: number) {
  const image = productImages.value[slideIndex.value + offset]
  return getOptimizedProductImageUrl(image?.iconUrl || '', 705)
}

// 移动端规格弹层沿用极雾详情页的商品图比例，按 2x 像素请求以兼顾清晰度和资源体积。
const mobileSkuPickerImageUrl = computed(() => {
  return getOptimizedProductImageUrl(
    selectedSku.value?.iconUrl || currentImageSourceUrl.value,
    200,
  )
})

// 获取缩略图 URL（应用 CDN 域名替换和 transferOssUrl）
const getThumbImageUrl = (iconUrl: string) => {
  if (!iconUrl) return ''
  const processedUrl = transferOssUrl(iconUrl, 150)
  return replaceCdnDomain(processedUrl)
}
// 立即购买方法 - 使用专门的立即购买接口
async function jumpPayPage(options: { fromPicker?: boolean } = {}) {
  if (isMobileLayout.value && !options.fromPicker) {
    await openMobileSkuPicker('buy')
    return
  }

  if (await requestSkuSelection('buy')) return

  if (!currentSkuAvailable.value) {
    ElMessage.warning($t('messages.specSoldOut'))
    return
  }

  // 检查是否有有效的SKU ID：优先使用选中的SKU，否则回退到第一个可用的SKU
  let skuId = selectedSku.value?.id
  if (!skuId) {
    // 如果没有选中的SKU，尝试从SKU列表中获取第一个可用的SKU
    const firstAvailableSku = productInfo.value.dealerItemSkuDTOList?.find(
      (sku: any) => sku.state !== 0 && sku.inventory > 0
    )
    skuId = firstAvailableSku?.id
  }
  if (!skuId) {
    ElMessage.warning($t('messages.cannotGetSkuInfoRefresh'))
    return
  }

  try {
    const loadingInstance = ElLoading.service({
      lock: true,
      text: $t('messages.confirmingOrder'),
      background: 'rgba(255, 255, 255, 0.5)',
    })

    // 构建规格信息字符串
    const specsInfo = buildSpecsInfo()

    // 构建立即购买的订单参数
    const confirmOrderData = {
      countryCode:"CN",
      confirmOrderSkuReqDTOList: [{
        countryCode:"CN",
        dealerShopLiveStreamerId: "", // 直播主播ID，立即购买为空
        dealerItemSkuId: skuId, // SKU ID
        spItemSkuSpecConfig: specsInfo, // 规格配置
        buyAmount: buyTotalQuantity.value, // 购买数量
        dealerItemSpuTitle: productInfo.value.title || productInfo.value.subject, // 商品标题
        dealerItemSpuIconUrl: selectedSku.value?.iconUrl || productInfo.value.iconUrl, // 商品图片
        inventory: selectedSku.value?.inventory || productInfo.value.inventory || 0, // 库存
        payWay: productInfo.value.payWay,
        sendTypes: productInfo.value.sendTypes // 配送方式
      }]
    }

    // 调用立即购买确认订单API
    const response = await buyNowConfirmOrder(confirmOrderData)

    loadingInstance.close()

    if (response.success) {
      // ElMessage.success('訂單確認成功，正在跳轉到支付頁面')

      // 将API返回的购物车数据存储到localStorage，供支付页面使用
      if (response.data) {
        const paymentCartData = {
          originalTotalAmount: response.data.originalTotalAmount || 0,
          realTotalAmount: response.data.realTotalAmount || 0,
          availableDiscounts :  response.data.availableDiscounts||[] , 
          bestDiscount :  response.data.bestDiscount , 
          otherData:response.data,
          estimatedDiscountAmount :  response.data.estimatedDiscountAmount , 
          fullAmountGiftMatchTitle: response.data.fullAmountGiftMatchTitle,
          fullAmountGiftDiscountAmount: response.data.fullAmountGiftDiscountAmount,
          originalTransportationExpenses: response.data.originalTransportationExpenses,
          realTransportationExpenses: response.data.realTransportationExpenses,
          confirmOrderSkuRespDTOList: response.data.confirmOrderSkuRespDTOList || [],
          confirmShopInfoRespDTOList: response.data.confirmShopInfoRespDTOList || null,
          payWay: productInfo.value.payWay,
          sendTypes: productInfo.value.sendTypes, // 配送方式
        }
        console.log('paymentCartData', paymentCartData)
        // 存储到localStorage
        if (process.client) {
          localStorage.setItem('cart', JSON.stringify(paymentCartData))
        }
      }

      if (props.isModal)
        emit('buyNowSuccess')

      // 跳转到支付页面
      await router.push({
        path: '/payment',
        query: getRedirectTrackingQuery()
      })
    } else {
      ElMessage.error(response.msg || $t('messages.orderConfirmFailed'))
    }
  } catch (error: any) {
    // 显示更具体的错误信息
    let errorMessage = $t('messages.buyNowFailed')
    if (error.response?.data?.msg) {
      errorMessage = error.response.data.msg
    } else if (error.message) {
      errorMessage = error.message
    }

    ElMessage.error(errorMessage)
  }
}

// 构建规格信息字符串
function buildSpecsInfo() {
  const specs: string[] = []
  
  // 如果没有选中任何规格，返回空字符串
  if (!selectedAttributes.value || Object.keys(selectedAttributes.value).length === 0) {
    return ''
  }
  
  Object.keys(selectedAttributes.value).forEach(specGroupId => {
    const specValueId = selectedAttributes.value[specGroupId]
    
    // 从 dealerItemSkuSpecDTOs 查找规格组
    const specGroup = productInfo.value.dealerItemSkuSpecDTOs?.find((s: any) => s.id === specGroupId)
    
    if (specGroup) {
      // 在规格组的子项中查找规格值
      const specValue = specGroup.dealerItemSkuSpecReqDTOs?.find((v: any) => v.id === specValueId)
      
      if (specValue) {
        specs.push(`${specGroup.specName}:${specValue.specName}`)
      }
    }
  })
  
  return specs.join(',')
}

</script>

<template>
  <div class="pdp figma-product-detail" :class="{
    'small-screen': isSmallScreen,
    'is-modal': isModal,
  }">
    <div class="content" :class="{ 'modal-content': isModal }">
      <div class="gallery">

        <div class="hero" :style="heroStyle">
          <div
            class="hero-blank"
            :class="{ 'is-touch-dragging': isProductGalleryDragging }"
            @click.capture="onProductGalleryClickCapture"
            @mouseenter="showZoomButton = true"
            @mouseleave="showZoomButton = false"
            @touchstart.passive="onProductGalleryTouchStart"
            @touchmove.passive="onProductGalleryTouchMove"
            @touchend="onProductGalleryTouchEnd"
            @touchcancel="onProductGalleryTouchCancel"
          >
            <div
              class="hero-swipe-track"
              :class="{ 'is-settling': isProductGallerySettling }"
              :style="productGalleryTrackStyle"
            >
              <div
                v-for="offset in productGallerySlideOffsets"
                :key="offset"
                class="hero-swipe-slide"
              >
                <el-image
                  v-if="productImages[slideIndex + offset]"
                  :preview-teleported="offset === 0"
                  class="hero-image"
                  :class="{ 'is-current': offset === 0 }"
                  :alt="`${productInfo.title || 'Product'} ${slideIndex + offset + 1}`"
                  :src="getProductGalleryImageUrl(offset)"
                  :initial-index="slideIndex"
                  :preview-src-list="offset === 0 ? productImagePreviewList : []"
                ></el-image>
              </div>
            </div>
            <!-- 放大按钮 -->
            <div class="zoom-button" :class="{ 'always-visible': isSmallScreen, 'hover-visible': !isSmallScreen && showZoomButton }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#0e1b4d"/>
                <path d="M12 9H10V7H12V9ZM12 13H10V11H12V13Z" fill="#0e1b4d"/>
              </svg>
            </div>
          </div>
        </div>
        <!-- 移动端页码指示器 -->
        <div class="mobile-pagination" v-if="isSmallScreen && !allSkuIconUrlsEmpty && productImages.length > 1">
          <div class="pagination-controls">
            <button class="nav-arrow" @click="previousImage" :disabled="slideIndex === 0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
              </svg>
            </button>
            <div class="page-indicator"><span>{{ slideIndex + 1 }}</span>/{{ productImages.length }}</div>
            <button class="nav-arrow" @click="nextImage" :disabled="slideIndex === productImages.length - 1">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
        
        <!-- PC端缩略图 -->
        <div class="thumbs-wrapper" v-if="!isSmallScreen && !allSkuIconUrlsEmpty" :class="{ 'has-scroll': showThumbScrollButtons }">
          <button 
            v-if="showThumbScrollButtons" 
            class="thumb-scroll-btn thumb-scroll-left" 
            @click="scrollThumbsLeft"
            :aria-label="$t('texts.scrollLeft')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
            </svg>
          </button>
          <el-scrollbar ref="thumbsRef" class="thumbs-scrollbar">
            <div class="thumbs" :class="{ 'scrollable': showThumbScrollButtons }">
              <button
                v-for="(item, index) in productImages"
                :key="item.id || item.iconUrl"
                type="button"
                class="thumb"
                :class="{ active: slideIndex === index }"
                :aria-label="`${productInfo.title || ''} ${index + 1}`"
                @click="handleSkuImageClick(index)"
              >
                <img
                  class="max-w-full h-full m-auto"
                  referrerpolicy="no-referrer"
                  :src="getThumbImageUrl(item.iconUrl)"
                  :alt="`${productInfo.title || ''} ${index + 1}`"
                >
              </button>
            </div>
          </el-scrollbar>
          <button 
            v-if="showThumbScrollButtons" 
            class="thumb-scroll-btn thumb-scroll-right" 
            @click="scrollThumbsRight"
            :aria-label="$t('texts.scrollRight')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
      <!-- 商品信息 -->
      <div class="summary" ref="summaryRef">
        <h1 class="title">{{ productInfo.title || "" }}</h1>
        <div class="price-block">
          <div class="line price-row price-row--amount">
            <div class="price-values" :class="{ 'has-member-price': showMemberPrice }">
              <template v-if="showMemberPrice">
                <div class="price-line non-member">
                  <span class="member-label">非會員價</span>
                  <span class="price-amount">
                    <span class="price-currency">NT$</span>
                    <span class="price-number">{{ formatProductDisplayAmount(currentPrice) }}</span>
                  </span>
                </div>
                <div class="price-line member">
                  <img class="vip-icon" :src="vipIconUrl" alt="VIP" />
                  <span class="price-amount">
                    <span class="price-currency">NT$</span>
                    <span class="price-number">{{ formatProductDisplayAmount(currentMemberPrice) }}</span>
                  </span>
                </div>
              </template>
              <template v-else>
                <span class="price-standard-line">
                  <span class="price-caption">{{ $t('texts.salePrice') }}</span>
                  <span class="value current price-label">
                    <span class="price-currency">NT$</span>
                    <span class="price-number">{{ formatProductDisplayAmount(currentPrice) }}</span>
                  </span>
                </span>
              </template>
            </div>
            <div class="badges">
              <span v-if="purchaseActionDisabled" class="badge">{{ $t('texts.soldOut') }}</span>
            </div>
          </div>
          <div class="line product-meta-row">
            <el-rate :model-value="ratingValue" allow-half :disabled="true" />
            <button type="button" class="review-count" @click="scrollToReviews">
              {{ commentNum || '0' }} {{ $t('texts.reviewsCount') }}
            </button>
            <span v-if="displaySalesCount > 0" class="product-meta-divider" aria-hidden="true" />
            <span v-if="displaySalesCount > 0" class="sales-count">
              {{ $t('texts.soldCount', { count: displaySalesCountText }) }}
            </span>
          </div>
        </div>
        <ProductAppBenefits />
        <div v-if="productSpecs.length" ref="optionsRef" class="product-options">
          <div class="detail-section-divider" />

          <!-- PC：Figma 使用纯文字胶囊，不显示 SKU 图片。 -->
          <div class="desktop-product-options">
            <div class="option" v-for="(spec, index) in productSpecs" :key="index">
              <div class="option-heading">
                <div class="label">{{ $t('actions.select') }}{{ spec.specName }}：</div>
              </div>
              <div class="option-frame">
                <div
                  class="option-values-scrollbar"
                  role="region"
                  :aria-label="spec.specName"
                >
                  <div class="option-values">
                    <button
                      v-for="attrValue in spec.children"
                      :key="attrValue.id"
                      type="button"
                      class="option-iteme text-only"
                      :class="[
                        attrValueIsSelected(spec.id, attrValue.id) ? 'active' : '',
                        !isSpecValueAvailable(spec.id, attrValue.id) ? 'sold-out' : ''
                      ]"
                      :aria-pressed="attrValueIsSelected(spec.id, attrValue.id)"
                      :disabled="!isSpecValueAvailable(spec.id, attrValue.id)"
                      @click="handleSelectSpecValue(spec, attrValue)"
                    >
                      <span>{{ attrValue.specName }}</span>
                      <span
                        v-if="!isSpecValueAvailable(spec.id, attrValue.id)"
                        class="option-stock-badge"
                        aria-hidden="true"
                      >
                        {{ $t('texts.outOfStockLabel') }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 移动端：页面内仅展示图片横滑入口，点击后打开完整 SKU 面板。 -->
          <div class="mobile-option-previews">
            <div class="mobile-option-preview" v-for="(spec, index) in productSpecs" :key="index">
              <div class="mobile-option-preview__heading">
                <span class="mobile-option-preview__heading-label">
                  {{ $t('actions.select') }}{{ spec.specName }}：
                </span>
                <button
                  type="button"
                  class="mobile-option-preview__status"
                  :class="{
                    'is-unavailable': selectedSkuUnavailable,
                    'is-all-sold-out': allSkusUnavailable
                  }"
                  :aria-label="`${$t('actions.select')}${spec.specName}`"
                  aria-haspopup="dialog"
                  :disabled="allSkusUnavailable"
                  @click="openMobileSkuPicker()"
                >
                  <template v-if="allSkusUnavailable">
                    {{ $t('texts.allOptionsUnavailable') }}
                  </template>
                  <template v-else>
                    {{ selectedSpecValueText(spec) || $t('texts.pleaseSelect') }}
                    <img
                      class="mobile-option-preview__chevron"
                      src="/icons/figma-chevron-right-blue.svg"
                      alt=""
                      aria-hidden="true"
                    >
                  </template>
                </button>
              </div>
              <button
                type="button"
                class="mobile-option-preview__card"
                :class="{
                  'is-unavailable': selectedSkuUnavailable,
                  'is-all-sold-out': allSkusUnavailable
                }"
                :aria-label="`${$t('actions.select')}${spec.specName}`"
                aria-haspopup="dialog"
                :disabled="allSkusUnavailable"
                @click="openMobileSkuPicker()"
              >
                <span class="mobile-option-preview__frame">
                  <span
                    v-if="spec.children.some((item: any) => item.skuImage)"
                    class="mobile-option-preview__images"
                    aria-hidden="true"
                  >
                    <span
                      v-for="(attrValue, previewIndex) in spec.children.filter((item: any) => item.skuImage)"
                      :key="attrValue.id"
                      class="mobile-option-preview__image"
                      :class="{
                        'is-active': attrValueIsSelected(spec.id, attrValue.id)
                          || (!selectedSpecValueText(spec) && previewIndex === 0),
                        'is-sold-out': !isSpecValueAvailable(spec.id, attrValue.id)
                      }"
                    >
                      <img :src="getThumbImageUrl(attrValue.skuImage)" :alt="attrValue.specName">
                    </span>
                  </span>
                  <span v-else class="mobile-option-preview__text-options" aria-hidden="true">
                    <span
                      v-for="attrValue in spec.children.slice(0, 5)"
                      :key="attrValue.id"
                      :class="{ 'is-active': attrValueIsSelected(spec.id, attrValue.id) }"
                    >
                      {{ attrValue.specName }}
                    </span>
                  </span>
                </span>
              </button>
            </div>
          </div>

          <div
            v-if="allSkusUnavailable || selectedSkuUnavailable"
            class="option-stock-alert"
            role="status"
          >
            {{ allSkusUnavailable
              ? $t('texts.allOptionsUnavailableHint')
              : $t('texts.selectedOptionUnavailableHint') }}
          </div>

          <div class="detail-section-divider" />
        </div>

        <GoodsDetailBanner :alt="productInfo.title || ''" />

        <div ref="purchaseActionsRef" class="actions">
          <el-button class="add-to-cart" :class="{ 'sold-out-button': purchaseActionDisabled }" type="primary" :disabled="purchaseActionDisabled"
            @click="handleAddToCart">
            {{ $t('actions.addBuyCart') }}
          </el-button>
          <el-button
            v-if="shouldOfferReselection"
            class="buy-now"
            style="margin-left: 0;"
            type="success"
            @click="handleReselectOptions"
          >
            {{ $t('actions.reselectOptions') }}
          </el-button>
          <el-button
            v-else
            class="buy-now"
            :class="{ 'sold-out-button': purchaseActionDisabled }"
            style="margin-left: 0;"
            type="success"
            :disabled="purchaseActionDisabled"
            @click="jumpPayPage"
          >
            {{ purchaseActionDisabled ? $t('texts.temporarilyUnavailable') : $t('actions.buyNow') }}
          </el-button>
        </div>

        <!-- <el-divider /> -->
        <div class="product-description" v-if="productInfo.simpleIntroduction && !isModal">
          <div v-html="productInfo.simpleIntroduction"></div>
        </div>
        <el-divider v-if="!isModal" />
      </div>
    </div>
    <RateItem
      v-if="!isModal"
      :product-id="productInfo.dealerShopId"
      :productInfo-spu-id="productInfo.id"
      :product-comment-id="productInfo.dealerInfoId"
      :product-name="productInfo.title"
      :product-brand="productInfo.brand"
      :product-image="currentImageUrl"
      @review-submitted="handleReviewSubmitted"
      ref="reviewsRef"
    />
    <ClientOnly>
      <AddToCartModal 
        v-if="!isModal"
        v-model="cartDialogOpen" 
        mode="view" 
        :product-info="productInfo" 
        :selected-sku="selectedSku"
        :selected-attributes="selectedAttributes" 
        :buy-total-quantity="buyTotalQuantity" 
      />
    </ClientOnly>
    <Transition name="mobile-actions">
      <div v-if="showMobileStickyActions" class="mobile-sticky-actions">
        <div class="mobile-sticky-actions__buttons">
          <button
            type="button"
            class="mobile-sticky-actions__cart"
            :disabled="purchaseActionDisabled"
            @click="handleAddToCart"
          >
            {{ $t('actions.addBuyCart') }}
          </button>
          <button
            v-if="shouldOfferReselection"
            type="button"
            class="mobile-sticky-actions__buy"
            @click="handleReselectOptions"
          >
            {{ $t('actions.reselectOptions') }}
          </button>
          <button
            v-else
            type="button"
            class="mobile-sticky-actions__buy"
            :disabled="purchaseActionDisabled"
            @click="jumpPayPage"
          >
            {{ purchaseActionDisabled ? $t('texts.temporarilyUnavailable') : $t('actions.fastToBuy') }}
          </button>
        </div>
      </div>
    </Transition>

    <Teleport to="body">
      <Transition name="mobile-sku-picker">
        <div
          v-if="mobileSkuPickerOpen"
          class="mobile-sku-picker__overlay"
          @click.self="closeMobileSkuPicker"
          @touchmove="handleMobileSkuPickerOverlayTouchMove"
        >
          <section
            class="mobile-sku-picker"
            role="dialog"
            aria-modal="true"
            :aria-label="$t('actions.selectOptions')"
          >
            <div class="mobile-sku-picker__handle" aria-hidden="true" />
            <button
              ref="mobileSkuPickerCloseRef"
              type="button"
              class="mobile-sku-picker__close"
              :aria-label="$t('actions.close')"
              @click="closeMobileSkuPicker"
            >
              <img src="/icons/figma-close-x-navy.svg" alt="" aria-hidden="true">
            </button>

            <div class="mobile-sku-picker__body">
              <div class="mobile-sku-picker__header">
                <div class="mobile-sku-picker__product">
                  <img
                    v-if="mobileSkuPickerImageUrl"
                    class="mobile-sku-picker__product-image"
                    :src="mobileSkuPickerImageUrl"
                    :alt="productInfo.title || ''"
                  >
                  <div class="mobile-sku-picker__product-info">
                    <div class="mobile-sku-picker__title" :title="productInfo.title || ''">
                      {{ productInfo.title || '' }}
                    </div>
                    <div class="mobile-sku-picker__price">
                      <span class="mobile-sku-picker__sale-price">
                        <span class="mobile-sku-picker__currency">NT$</span>
                        <span class="mobile-sku-picker__amount">{{ formatProductDisplayAmount(currentPrice) }}</span>
                      </span>
                      <del v-if="currentOriginalPrice">
                        NT${{ formatProductDisplayAmount(currentOriginalPrice) }}
                      </del>
                    </div>
                    <div class="mobile-sku-picker__selection">
                      <template v-if="selectedSpecSummary">
                        {{ $t('goodsImagesEditor.selected') }}：{{ selectedSpecSummary }}
                      </template>
                      <template v-else>
                        {{ $t('texts.optionNotSelected') }}
                      </template>
                    </div>
                  </div>
                </div>

                <div class="mobile-sku-picker__quantity">
                  <QuantitySelector
                    v-model="buyTotalQuantity"
                    :min="1"
                    :max="quantityMax"
                    :step="1"
                    :disabled="quantityDisabled"
                    size="default"
                    @change="handleChange"
                  />
                </div>
              </div>

              <div class="mobile-sku-picker__options">
                <div
                  v-for="(spec, index) in productSpecs"
                  :key="index"
                  class="mobile-sku-picker__option"
                >
                  <div class="mobile-sku-picker__option-label">
                    {{ spec.specName || '屬性' }}
                  </div>
                  <div
                    class="mobile-sku-picker__values"
                    role="region"
                    tabindex="0"
                    :aria-label="spec.specName"
                  >
                    <button
                      v-for="attrValue in spec.children"
                      :key="attrValue.id"
                      type="button"
                      class="mobile-sku-picker__pill"
                      :class="{
                        'is-active': attrValueIsSelected(spec.id, attrValue.id),
                        'is-sold-out': !isSpecValueAvailable(spec.id, attrValue.id)
                      }"
                      :aria-pressed="attrValueIsSelected(spec.id, attrValue.id)"
                      :disabled="!isSpecValueAvailable(spec.id, attrValue.id)"
                      @click="handleSelectSpecValue(spec, attrValue)"
                    >
                      <img
                        v-if="attrValue.skuImage"
                        class="mobile-sku-picker__option-image"
                        :src="getThumbImageUrl(attrValue.skuImage)"
                        :alt="attrValue.specName || ''"
                      >
                      <span>{{ attrValue.specName }}</span>
                      <span
                        v-if="!isSpecValueAvailable(spec.id, attrValue.id)"
                        class="option-stock-badge"
                        aria-hidden="true"
                      >
                        {{ $t('texts.outOfStockLabel') }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-if="allSkusUnavailable || selectedSkuUnavailable"
                class="mobile-sku-picker__stock-alert"
                role="status"
              >
                {{ allSkusUnavailable
                  ? $t('texts.allOptionsUnavailableHint')
                  : $t('texts.selectedOptionUnavailableHint') }}
              </div>
            </div>

            <div
              class="mobile-sku-picker__footer"
              :class="{ 'is-confirm-mode': pendingMobilePurchaseAction }"
            >
              <template v-if="pendingMobilePurchaseAction === 'cart'">
                <button
                  type="button"
                  class="mobile-sku-picker__action mobile-sku-picker__action--add"
                  :disabled="needsSkuSelection || purchaseActionDisabled"
                  @click="handleMobilePickerAddToCart"
                >
                  {{ $t('actions.addBuyCart') }}
                </button>
              </template>
              <template v-else-if="pendingMobilePurchaseAction === 'buy'">
                <button
                  type="button"
                  class="mobile-sku-picker__action mobile-sku-picker__action--buy"
                  :disabled="needsSkuSelection || purchaseActionDisabled"
                  @click="handleMobilePickerBuyNow"
                >
                  {{ purchaseActionDisabled ? $t('texts.temporarilyUnavailable') : $t('actions.fastToBuy') }}
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="mobile-sku-picker__action mobile-sku-picker__action--cart"
                  :disabled="purchaseActionDisabled"
                  @click="handleMobilePickerAddToCart"
                >
                  {{ $t('actions.addBuyCart') }}
                </button>
                <button
                  type="button"
                  class="mobile-sku-picker__action mobile-sku-picker__action--buy"
                  :disabled="purchaseActionDisabled"
                  @click="handleMobilePickerBuyNow"
                >
                  {{ purchaseActionDisabled ? $t('texts.temporarilyUnavailable') : $t('actions.fastToBuy') }}
                </button>
              </template>
            </div>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.pdp {
  min-width: stretch;
  width: 100%;

  margin: 0;
  padding: 0;
  background: #fff;
}

.pdp :deep(.el-rate.is-disabled .el-rate__item) {
  color: #e6e6e6;
}
.price-label{
  margin:5px 0px;
  font-size: 19px;
  color:rgb(14, 27, 77);
}

.product-related-products {
  width: min(1140px, calc(100% - 32px));
  margin: 8px auto 24px;
  overflow: hidden;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  background: #fff;
}

.mobile-sticky-actions {
  position: fixed;
  z-index: 1200;
  left: 0;
  right: 0;
  bottom: 0;
  display: none;
  padding: 5px 12px max(5px, env(safe-area-inset-bottom));
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background: #fff;
  box-shadow: none;
  font-family: Inter, "Noto Sans TC", sans-serif;
}

.mobile-sticky-actions__buttons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;
  max-width: 480px;
  height: 40px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 999px;
}

.mobile-sticky-actions button {
  min-width: 0;
  height: 40px;
  border: 0;
  border-radius: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  cursor: pointer;
}

.mobile-sticky-actions__cart {
  min-width: 0;
  width: auto;
  background: #2b2b2b;
  color: #fff;
}

.mobile-sticky-actions__buy {
  min-width: 0;
  width: auto;
  background: #ffd84d;
  color: #2b2b2b;
}

.mobile-sticky-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.mobile-actions-enter-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.mobile-actions-leave-active {
  transition: none;
}

.mobile-actions-enter-from,
.mobile-actions-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
/* 移动端适配 - 参考头部和底部的实现方式 */
@media (max-width: 768px) {
  .pdp {
    min-width: auto;
    width: 100%;
    overflow-x: hidden;
  }

  .mobile-sticky-actions {
    display: grid;
  }

  .product-related-products {
    width: calc(100% - 20px);
    margin-bottom: 16px;
  }
}

/* Product option contract from Figma:
 * desktop = text pills; mobile page = image preview; mobile drawer = text pills.
 */
.desktop-product-options {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (min-width: 769px) {
  .figma-product-detail .desktop-product-options .option-heading {
    justify-content: flex-start;
    align-items: flex-end;
    height: 36px;
    padding-top: 16px;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  .figma-product-detail .desktop-product-options .option-frame {
    margin-top: 0;
    padding-top: 20px;
  }

  .figma-product-detail .desktop-product-options .option-values {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  .figma-product-detail .desktop-product-options .option-iteme,
  .figma-product-detail .desktop-product-options .option-iteme.text-only {
    width: auto;
    min-width: 0;
    height: 36px;
    min-height: 36px;
    padding: 0 20px;
    margin: 0;
    overflow: visible;
    border: 1px solid rgba(14, 27, 77, 0.55);
    border-radius: 999px;
    background: #eff0f5;
    color: #0e1b4d;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    white-space: nowrap;
  }

  .figma-product-detail .desktop-product-options .option-iteme:hover:not(:disabled) {
    border-color: #0e1b4d;
    box-shadow: 0 0 0 1px #0e1b4d;
  }

  .figma-product-detail .desktop-product-options .option-iteme.active,
  .figma-product-detail .desktop-product-options .option-iteme.text-only.active {
    border-color: #0e1b4d;
    background: #0e1b4d;
    color: #fff;
  }

  .figma-product-detail .desktop-product-options .option-iteme.sold-out,
  .figma-product-detail .desktop-product-options .option-iteme.sold-out.active {
    border-color: rgba(14, 27, 77, 0.1);
    background: #eff0f5;
    color: rgba(14, 27, 77, 0.6);
    box-shadow: none;
    text-decoration: line-through;
    opacity: 1;
  }
}

.mobile-option-previews {
  display: none;
  flex-direction: column;
  gap: 18px;
}

.mobile-option-preview__heading {
  width: 100%;
  height: 36px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding-top: 16px;
  box-sizing: border-box;
  color: #111833;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.mobile-option-preview__heading-label {
  flex: 0 0 auto;
}

.mobile-option-preview__status {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #4770db;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
  background: transparent;
  white-space: nowrap;
}

.mobile-option-preview__status.is-unavailable,
.mobile-option-preview__status.is-all-sold-out {
  color: #aa3f00;
}

.mobile-option-preview__card {
  width: 100%;
  height: 98px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 0;
  padding: 12px 0 0;
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #111833;
  text-align: left;
  cursor: pointer;
  appearance: none;
}

.mobile-option-preview__card.is-all-sold-out {
  opacity: 0.45;
  cursor: not-allowed;
}

.mobile-option-preview__card:focus-visible {
  outline: 2px solid var(--primary-color, #4770db);
  outline-offset: 2px;
}

.mobile-option-preview__frame {
  width: 100%;
  height: 86px;
  flex: 0 0 86px;
  display: flex;
  align-items: flex-start;
  padding: 10px;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f8f8;
}

.mobile-option-preview__images {
  width: 100%;
  height: 62px;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  scrollbar-width: none;
  overscroll-behavior-inline: contain;
  touch-action: pan-x;
}

.mobile-option-preview__images::-webkit-scrollbar {
  display: none;
}

.mobile-option-preview__image {
  width: 62px;
  height: 62px;
  min-width: 62px;
  flex: 0 0 62px;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #f2f5ff;
}

.mobile-option-preview__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-option-preview__image.is-active {
  border-color: var(--primary-color, #4770db);
}

.mobile-option-preview__image.is-sold-out {
  opacity: 0.42;
  filter: grayscale(1);
}

.mobile-option-preview__text-options {
  height: 62px;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.mobile-option-preview__text-options > span {
  min-width: max-content;
  padding: 8px 20px;
  border: 1px solid rgba(14, 27, 77, 0.55);
  border-radius: 999px;
  background: #eff0f5;
}

.mobile-option-preview__text-options > span.is-active {
  background: #0e1b4d;
  color: #fff;
}

.mobile-option-preview__chevron {
  width: 16px;
  height: 16px;
  display: block;
  padding: 3px 5px;
  box-sizing: border-box;
}

.mobile-sku-picker__overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(17, 19, 21, 0.35);
  overscroll-behavior: none;
  touch-action: none;
}

.mobile-sku-picker {
  position: relative;
  width: min(100%, 480px);
  height: auto;
  min-height: 0;
  max-height: 82dvh;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
  background: #fff;
  color: #111315;
  padding: 8px 16px 18px;
  box-sizing: border-box;
  box-shadow: 0 -8px 24px rgba(17, 19, 21, 0.08);
  font-family: Inter, "Noto Sans TC", sans-serif;
  overscroll-behavior: contain;
  touch-action: auto;
}

.mobile-sku-picker__close {
  position: absolute;
  top: 14px;
  right: 10px;
  z-index: 2;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.mobile-sku-picker__handle {
  align-self: center;
  width: 36px;
  height: 4px;
  flex: 0 0 4px;
  margin-bottom: 8px;
  border-radius: 2px;
  background: #9b9b9b;
}

.mobile-sku-picker__close img {
  width: 20px;
  height: 20px;
  display: block;
}

.mobile-sku-picker__body {
  width: 100%;
  height: auto;
  min-height: 0;
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 0;
  box-sizing: border-box;
}

.mobile-sku-picker__header {
  width: 100%;
  height: auto;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 24px 12px 0;
  box-sizing: border-box;
  background: #fff;
}

.mobile-sku-picker__product {
  width: 100%;
  min-height: 100px;
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.mobile-sku-picker__product-image {
  width: 100px;
  height: 100px;
  max-width: 100px;
  max-height: 100px;
  flex: 0 0 100px;
  display: block;
  border: 1px solid #f0eef6;
  border-radius: 8px;
  background: #f8f9fa;
  object-fit: cover;
}

.mobile-sku-picker__product-info {
  min-width: 0;
  flex: 1 1 auto;
  padding: 0;
}

.mobile-sku-picker__price {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
  color: #18795e;
  line-height: 24px;
}

.mobile-sku-picker__title {
  min-width: 0;
  margin-bottom: 4px;
  overflow: hidden;
  color: #111315;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.mobile-sku-picker__sale-price {
  display: inline-flex;
  align-items: baseline;
}

.mobile-sku-picker__currency {
  font-size: 14px;
  font-weight: 600;
  line-height: 29px;
}

.mobile-sku-picker__amount {
  font-size: 20px;
  font-weight: 700;
  line-height: 29px;
}

.mobile-sku-picker__price del {
  color: #a9afbd;
  font-size: 16px;
  font-weight: 400;
}

.mobile-sku-picker__selection {
  margin-top: 4px;
  color: #496877;
  display: inline-block;
  max-width: 100%;
  padding: 1px 8px;
  border-radius: 3px;
  background: #f3f7f9;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
}

.mobile-sku-picker__quantity {
  width: 78px;
  height: 32px;
  flex: 0 0 auto;
  align-self: flex-end;
  margin: -40px 0 0;
}

.mobile-sku-picker__quantity :deep(.quantity-selector) {
  width: 78px;
  height: 32px;
}

.mobile-sku-picker__quantity :deep(.el-input-number) {
  width: 78px;
  height: 32px;
}

.mobile-sku-picker__quantity :deep(.quantity-controls) {
  width: 78px;
  height: 32px;
}

.mobile-sku-picker__quantity :deep(.quantity-input),
.mobile-sku-picker__quantity :deep(.quantity-input-wrapper) {
  width: 78px;
  height: 32px;
}

.mobile-sku-picker__quantity :deep(.el-input__wrapper) {
  height: 32px;
  padding: 1px 6px;
  box-sizing: border-box;
  border-radius: 25px !important;
  box-shadow: 0 0 0 1px rgba(14, 27, 77, 0.08) inset;
}

.mobile-sku-picker__quantity :deep(.el-input__inner) {
  height: 30px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 400;
}

.mobile-sku-picker__quantity :deep(.control-btn) {
  width: 22px;
  min-width: 22px;
  height: 22px;
  min-height: 22px;
  margin: 0;
}

.mobile-sku-picker__options {
  width: 100%;
  height: auto;
  min-height: 0;
  flex: 0 1 auto;
  max-height: calc(82dvh - 190px);
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0 0 16px;
  box-sizing: border-box;
  overflow: hidden;
  background: #fff;
}

.mobile-sku-picker__option {
  min-height: 0;
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
}

.mobile-sku-picker__option-label {
  width: 100%;
  height: 36px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding-top: 10px;
  box-sizing: border-box;
  color: #0e1b4d;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.mobile-sku-picker__values {
  width: 100%;
  height: auto;
  min-height: 0;
  max-height: 270px;
  flex: 0 1 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 42px;
  align-content: flex-start;
  gap: 8px;
  margin: 0;
  padding: 8px 0 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: #aeb5c5 transparent;
  scrollbar-width: thin;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
}

.mobile-sku-picker__values::-webkit-scrollbar {
  width: 3px;
}

.mobile-sku-picker__values::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #aeb5c5;
}

.mobile-sku-picker__values::-webkit-scrollbar-track {
  background: transparent;
}

.mobile-sku-picker__pill {
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  padding: 4px 8px;
  text-align: left;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  background: #fff;
  color: #111315;
  font-size: 11px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  gap: 4px;
  overflow: hidden;
  appearance: none;
}

.mobile-sku-picker__option-image {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 4px;
  background: #f8f9fa;
  object-fit: cover;
}

.mobile-sku-picker__pill > span:first-of-type {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-sku-picker__pill.is-active {
  border-color: #496877;
  background: #f3f7f9;
  color: #304c59;
}

.mobile-sku-picker__pill.is-sold-out,
.mobile-sku-picker__pill.is-sold-out.is-active {
  border-color: #e1e4e8;
  background: #f1f3f5;
  color: #7c858e;
  opacity: 0.55;
}

.mobile-sku-picker__pill:focus-visible,
.mobile-sku-picker__close:focus-visible,
.mobile-sku-picker__action:focus-visible {
  outline: 2px solid var(--primary-color, #4770db);
  outline-offset: 2px;
}

.mobile-sku-picker__stock-alert {
  margin-top: 16px;
  padding: 8px 10px;
  border-radius: 4px;
  background: #fff7ea;
  color: #9a5700;
  font-size: 12px;
  line-height: 18px;
}

.mobile-sku-picker__footer {
  width: 100%;
  height: 73px;
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 13px 0 0;
  box-sizing: border-box;
  border-top: 1px solid #e1e4e8;
  background: #fff;
}

.mobile-sku-picker__footer.is-confirm-mode {
  grid-template-columns: minmax(0, 1fr);
}

.mobile-sku-picker__action {
  height: 48px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  cursor: pointer;
}

.mobile-sku-picker__action--cart {
  border: 1px solid #ff8a00;
  background: #ff8a00;
  color: #fff;
}

.mobile-sku-picker__action--add {
  border: 1px solid #ff8a00;
  background: #ff8a00;
  color: #fff;
}

.mobile-sku-picker__action--buy {
  border: 1px solid #ff4d00;
  background: #ff4d00;
  color: #fff;
}

.mobile-sku-picker__action:disabled {
  border-color: #d8dbe3;
  background: #eff0f3;
  color: #9ba2b3;
  cursor: not-allowed;
}

.mobile-sku-picker-enter-active,
.mobile-sku-picker-leave-active {
  transition: opacity 0.2s ease;
}

.mobile-sku-picker-enter-active .mobile-sku-picker,
.mobile-sku-picker-leave-active .mobile-sku-picker {
  transition: transform 0.22s ease;
}

.mobile-sku-picker-enter-from,
.mobile-sku-picker-leave-to {
  opacity: 0;
}

.mobile-sku-picker-enter-from .mobile-sku-picker,
.mobile-sku-picker-leave-to .mobile-sku-picker {
  transform: translateY(100%);
}

@media (min-width: 769px) {
  .mobile-sku-picker__overlay {
    display: none;
  }
}

@media (max-width: 768px) {
  .desktop-product-options {
    display: none;
  }

  .mobile-option-previews {
    display: flex;
  }
}

.content {
  display: grid;
  grid-template-columns: 1fr;
  padding-top: 20px;
  justify-content: center;
  gap: 20px;
  margin: 0 auto;
  padding-left: 16px;
  padding-right: 16px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

}

/* 移动端内容区域优化 */
@media (max-width: 768px) {
  .content {
    grid-template-columns: 1fr;
    padding: 15px 10px;
    gap: 15px;
    margin: 0;
  }
}

@media (min-width: 768px) {
  .content {
    grid-template-columns: 1fr;
    padding-top: 40px;
    gap: 40px;
    padding-left: 24px;
    padding-right: 24px;
    margin: 0 auto;
  }
}

@media (min-width: 1024px) {

  .content {
    grid-template-columns: minmax(400px, 500px) minmax(500px, 640px);
    padding-top: 60px;
    gap: 12px;
    padding-left: 0;
    padding-right: 0;
    margin: 0 auto;
    width: 100%;
    justify-content: center;
  }
}

@media (min-width: 1200px) {
  .content {
    grid-template-columns: 500px 640px;
    margin: 0 auto;
    width: 100%;
    justify-content: center;
  }
}

.gallery {
  position: sticky;
  top: 12px;
  align-self: start;
  width: 100%;
  min-width: 0;
  height: auto;
}

/* 移动端图片画廊优化 */
@media (max-width: 768px) {
  .gallery {
    position: static;
    order: 1;
    margin-bottom: 0px;
  }

  .gallery .hero-blank {
    touch-action: pan-y;
    overscroll-behavior-x: contain;
  }

  .gallery .hero-blank.is-touch-dragging {
    cursor: grabbing;
  }
}

@media (max-width: 1023px) {
  .gallery {
    position: static;
    order: 1;
  }
}

.gallery .hero {
  max-width: 100%;
  border-radius: 8px;
  cursor: zoom-in;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .gallery .hero {
    /* margin: 0; */
  }
}


.gallery .hero-blank {
  width: 100%;
  height: auto;
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 18px;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-swipe-track {
  width: 100%;
  height: 100%;
  display: flex;
  flex: 0 0 100%;
  will-change: transform;
}

.hero-swipe-track.is-settling {
  transition: transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

.hero-swipe-slide {
  width: 100%;
  min-width: 100%;
  height: 100%;
  display: flex;
  flex: 0 0 100%;
  align-items: center;
  justify-content: center;
}

.hero-image {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.hero-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}

.thumbs-wrapper {
  position: relative;
  margin-top: 14px;
}

.thumbs {
  display: grid;
  text-align: center;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-left: auto;
  margin-right: auto;
  scroll-behavior: smooth;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.thumbs::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

@media (min-width: 480px) {
  .thumbs {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }
}

@media (min-width: 768px) {
  .thumbs {
    margin-left: 0;
    margin-right: 0;
  }
}

@media (min-width: 1024px) {
  .thumbs {
    margin-left: 0;
    margin-right: 0;
  }
}

/* 当超过4张时，改为横向滚动布局 */
.thumbs.scrollable {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 6px;
}

.thumbs.scrollable .thumb {
  flex-shrink: 0;
  width: calc((100% - 18px) / 4); /* 4个缩略图，减去3个gap */
  min-width: 100px; /* 最小宽度保证 */
}

.thumb-scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  z-index: 10;
  color: #0e1b4d;
}

.thumb-scroll-btn:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-50%) scale(1.1);
}

.thumb-scroll-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.thumb-scroll-left {
  left: -18px;
}

.thumb-scroll-right {
  right: -18px;
}

.thumb {
  aspect-ratio: 1/1;
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 18px;
  box-sizing: border-box;
  overflow: hidden;
  cursor: pointer;
}

.thumb.active {
  border: 1px solid #111;
}

.thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #999;
}

.summary {
  position: relative;
  overflow: visible;
  padding-right: 0;
  order: 1;
  z-index: 1;
  min-width: 0;
}

/* 移动端商品信息优化 */
@media (max-width: 768px) {
  .summary {
    position: static;
    max-height: none;
    overflow: visible;
    order: 2;
    padding: 20px 15px;
    /* background: #fff; */
    border-top: 1px solid #eee;
    /* border-radius: 12px 12px 0 0;
    box-shadow: 0 -2px 10px rgba(0,0,0,0.1); */
    z-index: 10;
  }
}

@media (max-width: 1023px) {
  .summary {
    position: static;
    max-height: none;
    overflow: visible;
    padding-right: 0;
    order: 1;
    text-align: center;
    background-color: transparent;
  }
  
  /* 小屏幕标题居中 */
  .title {
    text-align: center;
    color: #0e1b4d;
  }
  .tt-rating-text, .price-row{
    margin: auto;
    justify-content: center;
  }
  /* 小屏幕价格区域居中 */
  .price-block {
    text-align: center;
  }
  
  .price-row {
    justify-content: center;
  }
  
  /* 小屏幕评分居中 */
  .tt-rating-text {
    justify-content: center;
  }
  
  /* 小屏幕规格选择左对齐 */
  .option {
    text-align: left;
  }
  
  .option-iteme {
    margin: 0.5rem;
    display: inline-flex;
  }
  
  /* 小屏幕数量选择器居中 */
  .quantity {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  /* 小屏幕按钮居中 */
  .actions {
    align-items: center;
  }
}


.title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
  color: #0e1b4d;
}

/* 移动端标题优化 */
@media (max-width: 768px) {
  .title {
    font-size: 16px;
    line-height: 1.4;
    margin-bottom: 15px;
    text-align: center;
  }
}

@media (min-width: 768px) {
  .title {
    font-size: 24px;
  }
}

@media (min-width: 1024px) {
  .title {
    font-size: 24px;
  }
}

.price-block {
  margin-top: 12px;
}

/* 移动端价格区域优化 */
@media (max-width: 768px) {
  .price-block {
    margin: 15px 0;
    text-align: center;
  }
}

@media (min-width: 768px) {
  .price-block {
    margin-top: 8px;
  }
}

@media (min-width: 1024px) {
  .price-block {
    margin-top: 4px;
  }
}

.price-row {
  display: flex;
  align-items: baseline;
  /* justify-content: space-between; */
  padding: 6px 0;
  font-weight: 400;
  color: #0e1b4d
}

.price-values {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.price-values.has-member-price {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.price-line {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.price-line.non-member {
  color: rgba(14, 27, 77, 0.6);
  font-weight: 500;
}

.price-line.member {
  color: #0e1b4d;
  font-weight: 700;
}

.member-label {
  font-size: 12px;
}

.vip-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.price-row .label {
  color: #0e1b4d;
}

.tt-rating-text {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}


.tt-rating-text .demonstration {
  /* font-size: 14px; */
  color: #0e1b4d;
}

@media (min-width: 768px) {
  .tt-rating-text {
    gap: 4px;
    flex-wrap: nowrap;
  }

  .tt-rating-text .demonstration {
    font-size: 13px;
  }
}



.compare {
  color: #999;
  text-decoration: line-through;
}

.badge {
  border: 1px solid #eee;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  color: #999;
}

.badge.sale {
  color: #c45500;
  border-color: #f0d7c2;
}

.badges {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.unit {
  color: #666;
  font-size: 12px;
  margin-top: 4px;
}

.option {
  margin: 16px 0;
}

@media (min-width: 768px) {
  .option {
    margin: 12px 0;
  }
}

@media (min-width: 1024px) {
  .option {
    margin: 8px 0;
  }
}

.option-iteme {
  display: inline-block;
  margin: .7rem .5rem .2rem 0;
  padding: 12px 20px;
  font-size: 14px;
  letter-spacing: .1rem;
  line-height: 1;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  border-radius: 40px;
  color: #0e1b4d;
  border: 1px solid #0e1b4d;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 768px) {
  .option-iteme {
    padding: 8px 26px;
    min-height: auto;
    display: inline-block;
  }
}


.option-item.xz {
  background-color: #0e1b4d;
}

.label {
  font-size: 14px;
  font-weight: 500;
  color: #0e1b4d;
}

.quantity {
  margin: 20px 0;
}

@media (min-width: 768px) {
  .quantity {
    margin: 16px 0;
  }
}

@media (min-width: 1024px) {
  .quantity {
    margin: 12px 0;
  }
}

.quantity-number {
  width: 100px;
  height: 40px;
  border-radius: 26px
}

.quantity-number span {
  background: transparent;
}

.quantity .in-cart {
  margin-top: 4px;
  color: #666;
  font-size: 13px;
}

.actions {
  gap: 12px;
  margin: 20px 0 24px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: space-around;
}

/* 移动端操作按钮优化 */
@media (max-width: 768px) {
  .actions {
    flex-direction: column;
    gap: 12px;
    margin: 20px 0;
    justify-content: center;
  }
}

@media (min-width: 480px) {
  .actions {
    gap: 12px;
    margin: 16px 0 20px;
  }
  .option-iteme {
    padding: 8px 26px;
    min-height: auto;
    display: inline-block;
  }
}

@media (min-width: 768px) {
  .actions {
    gap: 8px;
    margin: 12px 0 16px;
  }
}

@media (min-width: 1024px) {
  .actions {
    gap: 8px;
    margin: 30px 0 10px;
  }
}

.actions button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 16px 24px;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 400;
  height: 48px;
  border-radius: 40px;
  text-decoration: none;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  appearance: none;
  min-width: 120px;
}

/* 移动端按钮优化 */
@media (max-width: 768px) {
  .actions button {
    width: 100%;
    height: 48px;
    font-size: 16px;
    min-width: auto;
    margin-bottom: 8px;
  }
}

@media (min-width: 480px) {
  .actions button {
    /* padding: 16px 32px; */
    height: 44px;
    font-size: 15px;
    width: 50%;
  }
}

@media (min-width: 768px) {
  .actions button {
    /* padding: 16px 42px; */
    /* min-width: 400px; */
    width: 80%;
    font-size: 14px;
  }
}

.actions button:active {
  transform: scale(0.98);
}

.add-to-cart {
  background: transparent;
  border: 1px solid #0e1b4d;
  color: #0e1b4d;
  transition: all 0.3s ease;
}

.add-to-cart:hover:not(:disabled) {
  box-shadow: 0 0 0 2px #0e1b4d;
}

.buy-now {
  background: var(--primary-color,#4770db);
  border: 1px solid var(--primary-color,#4770db);
  margin-left: 0px;
  transition: all 0.3s ease;
  color: var(--primary-text-color,#FFFFFF);
}

.buy-now:hover:not(:disabled) {
  box-shadow: 0 0 0 2px #4770db;
}

:deep(.el-button.add-to-cart.is-disabled) {
  background: #bbb !important;
  border-color: #bbb !important;
  color: #fff !important;
  opacity: 1;
}

/* 售罄按钮样式 - 只降低透明度，保持原色 */
.sold-out-button.is-disabled {
  cursor: not-allowed;
  opacity: 0.5 !important;
}

:deep(.el-button.buy-now.sold-out-button.is-disabled) {
  cursor: not-allowed;
  opacity: 0.5 !important;
}

:deep(.el-button.add-to-cart.sold-out-button.is-disabled) {
  cursor: not-allowed;
  opacity: 0.5 !important;
}


.product-description {
  margin: 20px 0;
  font-size: 14px;
  line-height: 1.6;
}

/* 移动端商品描述优化 */
@media (max-width: 768px) {
  .product-description {
    margin: 20px 0;
    font-size: 14px;
    line-height: 1.6;
    padding: 0 10px;
  }
}

/* 小屏幕手机优化 - 使用JavaScript检测，避免样式冲突 */
.pdp.small-screen .content {
  padding: 10px 8px;
  gap: 10px;
}

.pdp.small-screen .summary {
  padding: 15px 10px;
}

.pdp.small-screen .title {
  font-size: 18px;
  text-align: center;
}

.pdp.small-screen .option {
  text-align: left;
}

.pdp.small-screen .label {
  display: block;
  width: 100%;
  text-align: left;
}

.pdp.small-screen .option-iteme {
  margin-left: 0;
}

.pdp.small-screen .actions button {
  height: 44px;
  font-size: 15px;
}

.pdp.small-screen .product-description {
  font-size: 13px;
  padding: 0 5px;
}

@media (min-width: 768px) {
  .product-description {
    margin: 25px 0;
    font-size: 15px;
  }
}

@media (min-width: 1024px) {
  .product-description {
    margin: 25px 0;
    font-size: 16px;
  }
}

.product-description :deep(img) {
  width: 100%;
  height: auto;
}



/* tighten divider spacing within summary */
.summary :deep(.el-divider) {
  margin: 16px 0;
}

@media (min-width: 768px) {
  .summary :deep(.el-divider) {
    margin: 14px 0;
  }
}

@media (min-width: 1024px) {
  .summary :deep(.el-divider) {
    margin: 12px 0;
  }
}

.active {
  /* background-color: #0e1b4d; */
  color: #fff;
}

.option-iteme.active {
  background-color: #0e1b4d; 
  color: #fff;
}

/* 售罄规格样式 */
.option-iteme.sold-out {
  color: #bbb;
  border-color: #ddd;
  text-decoration: line-through;
  cursor: not-allowed;
  opacity: 0.6;
}

.option-iteme.sold-out:hover {
  border-color: #ddd;
  box-shadow: none;
}

/* 手机屏幕额外居中样式 */
@media (max-width: 480px) {
  .summary {
    padding: 0 16px;
  }
  
  .title {
     
 
    font-size: 24px !important;
  }
  .tt-rating-text, .price-row{
    margin: auto;
    justify-content: center;
  }
  
  .option-iteme {
    margin: 0.3rem;
    padding: 10px 26px;
    font-size: 13px;
    min-height: auto;
    display: inline-block;
  }
  
   .actions button {
    width: 80%;
  } 
}

@media screen and (min-width: 1024px) {
  .summary {
    margin-left: 50px;
    padding-left: 10px;
  }
}

/* 图片预览弹出框层级设置 */
:deep(.el-image-viewer__wrapper) {
  z-index: 9999 !important;
}

:deep(.el-image-viewer__mask) {
  z-index: 9998 !important;
}

/* 放大按钮样式 */
.zoom-button {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  transform: scale(0.8);
  z-index: 10;
  pointer-events: none;
}

/* 移动端常显示 */
.zoom-button.always-visible {
  opacity: 1;
  transform: scale(1);
}

/* PC端移入显示 */
.zoom-button.hover-visible {
  opacity: 1;
  transform: scale(1);
}

.zoom-button:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.zoom-button svg {
  transition: transform 0.2s ease;
}

.zoom-button:hover svg {
  transform: scale(1.1);
}

/* 移动端优化 */
@media (max-width: 768px) {
  .zoom-button {
    width: 36px;
    height: 36px;
    top: 10px;
    right: 10px;
  }
  
  .zoom-button svg {
    width: 20px;
    height: 20px;
  }
}

/* 移动端页码指示器样式 */
.mobile-pagination {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 16px;
 
}

.nav-arrow {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: #666;
}

.nav-arrow:hover:not(:disabled) {
  background: rgba(14, 27, 77, 0.1);
  color: #0e1b4d;
  transform: scale(1.1);
}

.nav-arrow:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.nav-arrow:active:not(:disabled) {
  transform: scale(0.95);
}

.page-indicator {
  font-size: 12px;
  font-weight: 500;
  color: #505a7c;
  min-width: 40px;
  text-align: center;
  user-select: none;
}

/* 隐藏PC端的页码指示器 */
@media (min-width: 769px) {
  .mobile-pagination {
    display: none;
  }
}

/* 弹窗模式下的内容高度 */
.content.modal-content {
  height: auto;
  max-height: none;
  overflow: visible;
}

@media (max-width: 1023px) {
  .thumbs-wrapper {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    box-sizing: border-box;
  }

  .thumbs-wrapper.has-scroll {
    padding: 0 42px;
  }

  .thumbs {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .thumbs.scrollable .thumb {
    flex: 0 0 calc((100% - 18px) / 4); /* 4个缩略图，减去3个gap */
    width: auto;
    min-width: 0;
  }

  .thumb-scroll-left {
    left: 6px;
  }

  .thumb-scroll-right {
    right: 6px;
  }

  .content.modal-content {
    overflow: visible;
    grid-template-columns: minmax(0, 1fr);
    align-content: start;
  }

  .content.modal-content .gallery,
  .content.modal-content .summary {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .content.modal-content .gallery {
    position: static;
  }

  .content.modal-content .gallery .hero {
    width: 100% !important;
  }
}

/* Figma product-detail layout: desktop node 102:21117, mobile node 72:23428 */
.figma-product-detail .detail-section-divider {
  height: 1px;
  width: 100%;
  margin: 0;
  flex: 0 0 1px;
  background: #d6dbeb;
}

.figma-product-detail .product-options {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
}

.figma-product-detail .option-frame {
  min-height: 0;
  margin-top: 12px;
  padding: 0;
  box-sizing: border-box;
  overflow: visible;
  background: transparent;
}

.figma-product-detail .price-row--amount {
  padding: 4px 0 6px;
}

.figma-product-detail .price-amount,
.figma-product-detail .price-label {
  display: inline-flex;
  align-items: baseline;
  margin: 0;
  color: #4770db;
  line-height: 36px;
}

.figma-product-detail .price-currency {
  font-size: 14px;
  font-weight: 600;
  line-height: 36px;
}

.figma-product-detail .price-number {
  font-size: 30px;
  font-weight: 700;
  line-height: 36px;
}

.figma-product-detail .product-meta-row {
  display: flex;
  align-items: center;
  min-height: 28px;
  gap: 10px;
  color: #505a7c;
  font-size: 13px;
  line-height: 20px;
}

.figma-product-detail .product-meta-row :deep(.el-rate) {
  height: 20px;
  line-height: 20px;
}

.figma-product-detail .product-meta-row :deep(.el-rate__icon) {
  margin-right: 2px;
  font-size: 18px;
}

.figma-product-detail .review-count {
  appearance: none;
  padding: 0;
  border: 0;
  border-bottom: 1px solid currentColor;
  background: transparent;
  color: #111833;
  font: inherit;
  line-height: 18px;
  cursor: pointer;
}

.figma-product-detail .product-meta-divider {
  width: 1px;
  height: 14px;
  flex: 0 0 1px;
  background: #d6dbeb;
}

.figma-product-detail .sales-count {
  white-space: nowrap;
}

.figma-product-detail .option-heading {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #111833;
  font-size: 14px;
  line-height: 20px;
}

.figma-product-detail .option-heading .label {
  color: #111833;
  font-weight: 500;
}

.figma-product-detail .option-heading__selection {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #a96100;
  text-align: right;
}

.figma-product-detail .option-heading__selection.is-unavailable {
  color: #b26300;
}

.figma-product-detail .option-heading__chevron {
  font-size: 20px;
  line-height: 16px;
}

.figma-product-detail .option-stock-alert {
  width: 100%;
  margin-top: -12px;
  padding: 8px 10px;
  box-sizing: border-box;
  border-radius: 2px;
  background: #fff7ea;
  color: #9a5700;
  font-size: 12px;
  line-height: 18px;
}

.figma-product-detail .option-iteme__image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.figma-product-detail .option-iteme.has-image > span {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.figma-product-detail .option-heading__selection {
  flex: 0 0 auto;
  white-space: nowrap;
}

.figma-product-detail .option-iteme.has-image {
  border-color: #e1e4eb;
  background: #fff;
}

.figma-product-detail .option-iteme.text-only {
  border-color: #0e1b4d;
  background: #fff;
}

.figma-product-detail .option-iteme.has-image.active {
  border: 2px solid var(--primary-color, #4770db);
  background: #f2f5ff;
  color: #111833;
}

.figma-product-detail .option-iteme.text-only.active {
  border-color: #0e1b4d;
  background: #0e1b4d;
  color: #fff;
}

.figma-product-detail .option-iteme.sold-out,
.figma-product-detail .option-iteme.sold-out.active {
  border-color: #e1e4eb;
  background: #f4f5f7;
  color: #9ba2b3;
  text-decoration: none;
  opacity: 1;
}

.figma-product-detail .option-iteme.sold-out .option-iteme__image {
  filter: grayscale(1);
  opacity: 0.42;
}

@media (min-width: 1024px) {
  .figma-product-detail .content {
    width: 100%;
    max-width: 1632px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    justify-content: center;
    gap: 0;
    padding: 56px 48px 38px;
    box-sizing: border-box;
  }

  .figma-product-detail .gallery {
    width: 100%;
    display: grid;
    grid-template-columns: 78px minmax(0, 668px);
    grid-template-rows: minmax(0, 1fr);
    align-items: stretch;
    gap: 22px;
    position: sticky;
    top: 16px;
  }

  .figma-product-detail .gallery .hero {
    grid-column: 2;
    grid-row: 1;
    width: 100% !important;
    max-width: 668px;
    min-width: 0;
    margin: 0;
    border-radius: 0;
  }

  .figma-product-detail .gallery .hero-blank {
    width: 100%;
    aspect-ratio: 1 / 1;
    border: 0;
    border-radius: 0;
  }

  .figma-product-detail .hero-image,
  .figma-product-detail :deep(.hero-image .el-image__inner) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .figma-product-detail .thumbs-wrapper {
    grid-column: 1;
    grid-row: 1;
    position: relative;
    align-self: stretch;
    min-width: 0;
    min-height: 0;
    height: auto;
    max-height: none;
    margin: 0;
    overflow: hidden;
  }

  .figma-product-detail .thumbs-scrollbar {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .figma-product-detail :deep(.thumbs-scrollbar .el-scrollbar__wrap) {
    overflow-x: hidden;
  }

  .figma-product-detail .thumbs {
    width: 76px;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-right: 2px;
    box-sizing: border-box;
  }

  .figma-product-detail .thumbs .thumb,
  .figma-product-detail .thumbs.scrollable .thumb {
    width: 76px;
    height: 76px;
    min-width: 76px;
    min-height: 76px;
    flex: 0 0 76px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: #fff;
  }

  .figma-product-detail .thumb.active {
    border: 1px solid #4770db;
    border-radius: 8px;
  }

  .figma-product-detail .thumb-scroll-btn {
    display: none;
  }

  .figma-product-detail .summary {
    width: 100%;
    min-width: 0;
    margin: 0;
    padding: 0 48px;
    box-sizing: border-box;
    text-align: left;
  }

  .figma-product-detail .title {
    font-size: 24px;
    line-height: 1.35;
  }

  .figma-product-detail .price-block {
    margin-top: 8px;
  }

  .figma-product-detail .option {
    display: block;
    margin: 0;
  }

  .figma-product-detail .option-heading {
    height: 36px;
    align-items: flex-end;
    padding-top: 16px;
    box-sizing: border-box;
  }

  .figma-product-detail .option-frame {
    margin-top: 0;
    padding-top: 20px;
  }

  .figma-product-detail .option .label {
    width: auto;
    height: auto;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .figma-product-detail .option-values {
    width: 100%;
    max-width: 500px;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
  }

  .figma-product-detail .option-values-scrollbar {
    width: 100%;
    height: auto;
    overflow: visible;
    outline: none;
  }

  .figma-product-detail .option-values--images {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: none;
    padding: 0;
    box-sizing: border-box;
  }

  .figma-product-detail .option-values-scrollbar:focus-visible {
    outline: 2px solid var(--primary-color, #4770db);
    outline-offset: 2px;
    border-radius: 2px;
  }

  .figma-product-detail .option-iteme {
    width: 62px;
    height: 62px;
    min-width: 62px;
    min-height: 62px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0;
    overflow: hidden;
    border: 1px solid #e1e4eb;
    border-radius: 8px;
    background: #f2f5ff;
    color: #111833;
    letter-spacing: 0;
  }

  .figma-product-detail .option-iteme.has-image > span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .figma-product-detail .option-iteme.text-only {
    width: auto;
    height: 36px;
    min-height: 36px;
    padding: 0 20px;
    border-color: rgba(14, 27, 77, 0.55);
    background: #eff0f5;
    font-size: 14px;
    line-height: 20px;
  }

  .figma-product-detail .option-iteme.has-image.active {
    border: 2px solid var(--primary-color, #4770db);
    background: #f2f5ff;
    color: #111833;
  }

  .figma-product-detail .option-iteme.text-only.active {
    border-color: #0e1b4d;
    background: #0e1b4d;
    color: #fff;
  }

  .figma-product-detail .option-iteme.sold-out,
  .figma-product-detail .option-iteme.sold-out.active {
    border-color: rgba(14, 27, 77, 0.1);
    background: #eff0f5;
    color: rgba(14, 27, 77, 0.6);
    text-decoration: line-through;
    opacity: 1;
  }

  .figma-product-detail .option-iteme.sold-out .option-iteme__image {
    filter: grayscale(1);
    opacity: 0.42;
  }

  .figma-product-detail .quantity {
    margin: 32px 0 0;
  }

  .figma-product-detail .actions {
    width: min(464px, 100%);
    gap: 12px;
    margin: 62px 0 18px;
  }

  .figma-product-detail .actions button {
    width: 100%;
    height: 48px;
  }
}

@media (max-width: 1023px) {
  .figma-product-detail .content {
    width: 100%;
    max-width: 760px;
    padding: 24px 16px 32px;
    box-sizing: border-box;
  }

  .figma-product-detail .summary {
    margin: 0;
    text-align: left;
  }

  .figma-product-detail .title,
  .figma-product-detail .price-block,
  .figma-product-detail .price-row,
  .figma-product-detail .tt-rating-text {
    text-align: left;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .figma-product-detail .content,
  .figma-product-detail.small-screen .content {
    max-width: none;
    gap: 0;
    padding: 0 0 28px;
  }

  .figma-product-detail .gallery {
    width: 100%;
    margin: 0;
  }

  .figma-product-detail .gallery .hero,
  .figma-product-detail .content.modal-content .gallery .hero {
    width: 100% !important;
    margin: 0;
  }

  .figma-product-detail .gallery .hero-blank {
    width: 100%;
    aspect-ratio: 1 / 1;
    border: 0;
    border-radius: 0;
  }

  .figma-product-detail .hero-image,
  .figma-product-detail :deep(.hero-image .el-image__inner) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .figma-product-detail .thumbs-wrapper {
    display: none;
  }

  .figma-product-detail .mobile-pagination {
    margin: 0;
    padding: 12px 0;
    border-bottom: 1px solid #eceef3;
  }

  .figma-product-detail .summary,
  .figma-product-detail.small-screen .summary {
    padding: 18px 16px 0;
    border: 0;
    text-align: left;
  }

  .figma-product-detail.is-modal .summary,
  .figma-product-detail.is-modal.small-screen .summary {
    padding: 18px 0 0;
  }

  .figma-product-detail .title,
  .figma-product-detail.small-screen .title {
    margin: 0 0 8px;
    font-size: 20px !important;
    line-height: 1.35;
    text-align: left;
  }

  .figma-product-detail .price-block {
    margin: 0;
    text-align: left;
  }

  .figma-product-detail .price-amount,
  .figma-product-detail .price-label {
    line-height: 34px;
  }

  .figma-product-detail .price-currency {
    font-size: 13px;
    line-height: 34px;
  }

  .figma-product-detail .price-number {
    font-size: 28px;
    line-height: 34px;
  }

  .figma-product-detail .price-row,
  .figma-product-detail .tt-rating-text {
    justify-content: flex-start;
  }

  .figma-product-detail .detail-section-divider {
    margin: 0;
  }

  .figma-product-detail .product-options {
    gap: 18px;
    margin-top: 12px;
  }

  .figma-product-detail .option,
  .figma-product-detail.small-screen .option {
    display: block;
    margin: 0;
    text-align: left;
  }

  .figma-product-detail .option-frame {
    min-height: 0;
    margin-top: 6px;
    padding: 0;
    overflow: visible;
    border-radius: 0;
    background: transparent;
  }

  .figma-product-detail .option-values-scrollbar {
    height: auto;
    overflow: visible;
  }

  .figma-product-detail .option-values,
  .figma-product-detail .option-values--images,
  .figma-product-detail.small-screen .option-values,
  .figma-product-detail.small-screen .option-values--images {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    min-width: 0;
    max-width: none;
    overflow: visible;
    padding: 0;
    scroll-snap-type: none;
  }

  .figma-product-detail .option-values-scrollbar.is-long {
    max-height: 224px;
    padding-right: 8px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-color: #b9bfcc transparent;
    scrollbar-width: thin;
  }

  .figma-product-detail .option-values-scrollbar.is-long::-webkit-scrollbar {
    width: 4px;
  }

  .figma-product-detail .option-values-scrollbar.is-long::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: #b9bfcc;
  }

  .figma-product-detail .option-values-scrollbar.is-long::-webkit-scrollbar-track {
    background: transparent;
  }

  .figma-product-detail .option .label {
    width: 100%;
    height: auto;
    margin: 0;
    padding: 0;
  }

  .figma-product-detail .option-iteme,
  .figma-product-detail.small-screen .option-iteme {
    width: auto;
    min-width: 0;
    min-height: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 15px;
    margin: 0;
    border-radius: 999px;
    font-size: 12px;
    letter-spacing: 0;
  }

  .figma-product-detail .option-iteme.has-image,
  .figma-product-detail.small-screen .option-iteme.has-image {
    width: 56px;
    min-width: 56px;
    height: 56px;
    min-height: 56px;
    padding: 0;
    border-radius: 8px;
  }

  .figma-product-detail .option-iteme__image {
    display: block;
  }

  .figma-product-detail .quantity,
  .figma-product-detail.small-screen .quantity {
    justify-content: flex-start;
    margin: 0;
    padding-top: 16px;
    box-sizing: border-box;
  }

  .figma-product-detail .quantity :deep(.quantity-selector),
  .figma-product-detail .quantity :deep(.quantity-input-wrapper),
  .figma-product-detail .quantity :deep(.quantity-input),
  .figma-product-detail .quantity :deep(.el-input-number),
  .figma-product-detail .quantity :deep(.quantity-controls) {
    width: 150px;
  }

  .figma-product-detail .quantity :deep(.quantity-input-wrapper),
  .figma-product-detail .quantity :deep(.quantity-input),
  .figma-product-detail .quantity :deep(.el-input-number),
  .figma-product-detail .quantity :deep(.el-input__wrapper),
  .figma-product-detail .quantity :deep(.quantity-controls) {
    height: 44px;
  }

  .figma-product-detail .quantity :deep(.el-input__inner) {
    height: 42px;
  }

  .figma-product-detail .quantity :deep(.control-btn) {
    height: 44px;
    min-height: 44px;
  }

  .figma-product-detail .actions {
    align-items: stretch;
    gap: 12px;
    margin: 24px 0 0;
  }

  .figma-product-detail .actions button,
  .figma-product-detail.small-screen .actions button {
    width: 100% !important;
    height: 48px;
    min-height: 48px;
    flex: 0 0 48px;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-size: 16px;
    line-height: 22px;
  }

  .figma-product-detail .product-description,
  .figma-product-detail.small-screen .product-description {
    padding: 0;
    margin: 22px 0;
  }
}

/* 08/21 DEV READY: product price, sold-out labels and campaign banner. */
.figma-product-detail .price-standard-line {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  white-space: nowrap;
}

.figma-product-detail .price-caption {
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.figma-product-detail .price-amount,
.figma-product-detail .price-label,
.mobile-sku-picker__price,
.mobile-sku-picker__sale-price {
  color: #18795e;
}

.figma-product-detail .desktop-product-options .option-iteme.sold-out,
.figma-product-detail .desktop-product-options .option-iteme.sold-out.active,
.mobile-sku-picker__pill.is-sold-out,
.mobile-sku-picker__pill.is-sold-out.is-active {
  overflow: visible;
  border-color: transparent;
  background: #f5f6fa;
  color: rgba(14, 27, 77, 0.16);
  box-shadow: none;
  opacity: 1;
  text-decoration: none;
}

.figma-product-detail .desktop-product-options .option-iteme.sold-out > span:first-child,
.mobile-sku-picker__pill.is-sold-out > span:first-child {
  text-decoration: line-through;
  text-decoration-thickness: from-font;
}

.option-stock-badge {
  position: absolute;
  z-index: 1;
  top: -4px;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 14px;
  padding: 2px 6px;
  border-radius: 4px;
  box-sizing: border-box;
  background: #d8dbeb;
  color: rgba(14, 27, 77, 0.55);
  font-size: 10px;
  font-weight: 400;
  line-height: 10px;
  text-decoration: none;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .figma-product-detail .price-caption {
    display: none;
  }

}
</style>
